import {
  defaultRecommendationAssumptionSet,
  defaultRecommendationOptions,
  recommendSetup,
  standardEquipmentCatalogue,
  type FrequencyCalibrationPoint,
  type PowerSource,
  type RecommendSetupInput,
  type RecommendationModelCoefficients,
  type RecommendationOptions,
  type RecommendationOptionsInput,
  type RecommendedSetup,
  type StandardEquipmentCatalogue,
} from "./configurator";
import { GLOSSARY } from "./glossary";

export interface RecommendationFormValues {
  selectedPowerSourceId: string;
  pipeOutsideDiameterMm: string;
  pipeWallThicknessMm: string;
  factoryCoatingThicknessMm: string;
  sideACutbackMm: string;
  sideBCutbackMm: string;
  ambientTemperatureC: string;
  startingTemperatureC: string;
  targetHeatTemperatureC: string;
  applicationProductName: string;
  subsequentProcessTemperatureC: string;
  coilClearanceMm: string;
  requiredOverhangMm: string;
}

type RecommendationModelCoefficientKey = keyof RecommendationModelCoefficients;

export type RecommendationOptionsFormValues = {
  referenceFrequencyHz: string;
  preferredTurnSpacingMm: string;
  minimumTurns: string;
  maximumTurns: string;
  coilClearanceMm: string;
  requiredOverhangMm: string;
  tieBarDiameterMm: string;
  frequencyCalibrationPoints: Array<{
    frequencyHz: string;
    heatingCycleFactor: string;
    cooldownFactor: string;
  }>;
  lowPowerUtilization: string;
  minimumCoilFrameSizeMarginMm: string;
  minimumCoilCoverageMarginMm: string;
  powerUtilizationBoundaryMargin: string;
} & Record<RecommendationModelCoefficientKey, string>;

export interface CataloguePowerSourceFormValues {
  id: string;
  name: string;
  powerSourceRatingKva: string;
  powerSourceFrequencyHz: string;
  maximumPowerSourceVoltageV: string;
  maximumPowerSourceCurrentA: string;
  minimumPowerUtilization: string;
  maximumPowerUtilization: string;
}

export interface CatalogueCoilFrameFormValues {
  id: string;
  name: string;
  coilFrameSizeInches: string;
  coilFrameInsideDiameterMm: string;
}

export interface CatalogueBottomEndFormValues {
  id: string;
  name: string;
  numberOfTurns: string;
  firstContactEdgeOffsetMm: string;
  lastContactEdgeOffsetMm: string;
  contactCenterSpacingsMm: string;
  bottomEndWidthMm: string;
  heatingResponseCelsiusMeterPerKwMinute: string;
}

export interface StandardEquipmentCatalogueFormValues {
  powerSources: CataloguePowerSourceFormValues[];
  coilFrames: CatalogueCoilFrameFormValues[];
  bottomEnds: CatalogueBottomEndFormValues[];
}

export interface CatalogueBottomEndDerivedValues {
  coilSectionMm: number;
  averageContactCenterSpacingMm: number;
  generatedTurnSpacingMm: number;
}

export interface CatalogueMaintenancePreview {
  generatedCatalogueCount: number;
  bottomEnds: CatalogueBottomEndDerivedValues[];
}

export type RecommendationResultRow = [label: string, value: string];

export interface DetailOperatingPointView {
  ratedLimits: {
    powerSourceRatingKva: string;
    powerSourceFrequencyHz: string;
    maximumPowerSourceVoltageV: string;
    maximumPowerSourceCurrentA: string;
  };
  operatingPoint: {
    powerSourceVoltageV: string;
    powerSourceCurrentA: string;
    powerFactor: string;
    deliveredPowerKw: string;
  };
  utilization: {
    displayValue: string;
    fillPercent: number | null;
    fillClass: string;
  };
  timing: {
    heatingCycleTimeMinutes: string;
    cooldownTimeMinutes: string | null;
  };
}

export interface RecommendationResultView {
  statusLabel: string;
  statusClassName: string;
  reason: string;
  summaryMetrics: RecommendationResultRow[];
  sections: {
    optimumCoil: RecommendationResultRow[];
    selectedEquipment: RecommendationResultRow[];
    calculatedBasis: RecommendationResultRow[];
    catalogueEvaluation: RecommendationResultRow[];
    advisoryCauses: RecommendationResultRow[];
    advisoryActions: RecommendationResultRow[];
    visibleAssumptions: RecommendationResultRow[];
  };
}

export type GeometryUnit = "mm" | "inch";
export type CoatingUnit = "um" | "mm" | "inch";

export interface SavedCoilConfiguration {
  id: string;
  reference: string;
  project: string;
  selectedPowerSourceId: string;
  fieldJointGeometry: {
    pipeOutsideDiameterMm: number;
    pipeWallThicknessMm: number;
    factoryCoatingThicknessMm: number;
    sideACutbackMm: number;
    sideBCutbackMm: number;
  };
  heating: {
    ambientTemperatureC: number;
    startingTemperatureC?: number;
    targetHeatTemperatureC: number;
  };
  applicationProduct?: {
    id: string;
    name: string;
    subsequentProcessTemperatureC?: number;
  };
  recommendation: RecommendedSetup;
  createdAt: Date;
}

export interface MeasurementRecord {
  id: string;
  configurationId: string;
  configurationReference: string;
  powerSourceLabel?: string;
  powerSourceFrequencyHz?: number;
  powerSourceVoltageV: number;
  powerSourceCurrentA: number;
  powerFactor: number;
  measuredTemperatureC: number;
  measuredHeatingCycleTimeMinutes?: number;
  measuredCooldownTimeMinutes?: number;
  predictedHeatingCycleTimeMinutes: number | null;
  predictedDeliveredPowerKw: number | null;
  measuredDeliveredPowerKw: number;
  notes: string;
  inFit: boolean;
  loggedAt: Date;
}

export interface MeasurementLogFormValues {
  configurationId: string;
  powerSourceVoltageV: string;
  powerSourceCurrentA: string;
  powerFactor: string;
  measuredTemperatureC: string;
  measuredHeatingCycleTimeMinutes: string;
  measuredCooldownTimeMinutes: string;
  notes: string;
}

export interface MeasuredDataAggregateView {
  recordCount: number;
  meanCycleTimeErrorPercent: string;
  meanDeliveredPowerErrorPercent: string;
  lastLoggedDate: string;
}

interface PersistedSavedCoilConfiguration
  extends Omit<SavedCoilConfiguration, "createdAt"> {
  createdAt: string;
}

interface PersistedMeasurementRecord
  extends Omit<MeasurementRecord, "loggedAt"> {
  loggedAt: string;
}

interface PersistedApplicationState {
  version: 1;
  standardEquipmentCatalogue: StandardEquipmentCatalogue;
  recommendationAssumptions: RecommendationOptionsInput;
  coilConfigurations: PersistedSavedCoilConfiguration[];
  measurementRecords: PersistedMeasurementRecord[];
  updatedAt: string;
}

export interface CalibratedCoefficientSuggestion {
  key: string;
  label: string;
  current: number;
  suggested: number;
  frequencyHz?: number;
}

export interface ConfigurationModalFormValues {
  configReference: string;
  configProject: string;
  modalSelectedPowerSourceId: string;
  modalPipeOutsideDiameter: string;
  modalPipeOutsideDiameterUnit: GeometryUnit;
  modalPipeWallThickness: string;
  modalPipeWallThicknessUnit: GeometryUnit;
  modalFactoryCoatingThickness: string;
  modalFactoryCoatingThicknessUnit: CoatingUnit;
  modalSideACutback: string;
  modalSideACutbackUnit: GeometryUnit;
  modalSideBCutback: string;
  modalSideBCutbackUnit: GeometryUnit;
  modalAmbientTemperatureC: string;
  modalStartingTemperatureC: string;
  modalTargetHeatTemperatureC: string;
  modalApplicationProductName: string;
  modalSubsequentProcessTemperatureC: string;
}

const modelCoefficientFields: Array<{
  key: RecommendationModelCoefficientKey;
  label: string;
}> = [
  { key: "powerUtilizationBase", label: "Power utilization base" },
  {
    key: "powerUtilizationFrequencyCoefficient",
    label: "Power utilization frequency coefficient",
  },
  {
    key: "powerUtilizationVoltageCoefficient",
    label: "Power utilization voltage coefficient",
  },
  {
    key: "powerUtilizationCurrentCoefficient",
    label: "Power utilization current coefficient",
  },
  {
    key: "powerUtilizationPipeOutsideDiameterCoefficient",
    label: "Power utilization pipe OD coefficient",
  },
  {
    key: "powerUtilizationPipeWallThicknessCoefficient",
    label: "Power utilization wall thickness coefficient",
  },
  {
    key: "powerUtilizationFrameDiameterCoefficient",
    label: "Power utilization frame diameter coefficient",
  },
  {
    key: "powerUtilizationTurnsCoefficient",
    label: "Power utilization turns coefficient",
  },
  {
    key: "powerUtilizationBottomEndWidthCoefficient",
    label: "Power utilization bottom end width coefficient",
  },
  {
    key: "powerUtilizationTurnSpacingCoefficient",
    label: "Power utilization turn spacing coefficient",
  },
  {
    key: "heatingCycleFrequencyCoefficient",
    label: "Heating cycle frequency coefficient",
  },
  {
    key: "cooldownFrequencyCoefficient",
    label: "Cooldown frequency coefficient",
  },
  {
    key: "cooldownHeatingCycleHeatSoakCoefficient",
    label: "Cooldown heat soak coefficient",
  },
  {
    key: "cooldownPipeWallThicknessCoefficient",
    label: "Cooldown wall thickness coefficient",
  },
];

let optionsFeedback: HTMLElement;
let assumptionsUnsavedState: HTMLElement;
let catalogueFeedback: HTMLElement;
let catalogueUnsavedState: HTMLElement;
let generatedCatalogueCount: HTMLElement;
let activeRecommendationOptions: RecommendationOptionsInput =
  copyRecommendationOptions(defaultRecommendationAssumptionSet);
let savedRecommendationOptions: RecommendationOptionsInput =
  copyRecommendationOptions(defaultRecommendationAssumptionSet);
let savedStandardEquipmentCatalogue: StandardEquipmentCatalogue =
  copyStandardEquipmentCatalogue(standardEquipmentCatalogue);
let activeStandardEquipmentCatalogue: StandardEquipmentCatalogue =
  copyStandardEquipmentCatalogue(standardEquipmentCatalogue);

const CONFIGURATIONS_PAGE_SIZE = 6;

let savedCoilConfigurations: SavedCoilConfiguration[] = [];
let configurationsCurrentPage = 1;
let configurationsSearchQuery = "";
let configurationsStatusFilter: string[] = ["recommended", "marginal", "not supported"];
let configurationModalMode: "create" | "edit" = "create";
let configurationModalEditId: string | null = null;
let currentDetailConfigId: string | null = null;
let dialogCtaCallback: (() => boolean | void) | undefined;
let savedMeasurementRecords: MeasurementRecord[] = [];

function formatPowerSourceShortName(name: string): string {
  return name.replace(/\s+power source$/i, "");
}

function powerSourceForConfiguration(config: SavedCoilConfiguration): PowerSource | undefined {
  return activeStandardEquipmentCatalogue.powerSources.find(
    (powerSource) => powerSource.id === config.selectedPowerSourceId,
  );
}

function createRaiaWorkbookConfiguration(): SavedCoilConfiguration | null {
  const selectedPowerSourceId = "tesi-ihg-120";
  const fieldJointGeometry = {
    pipeOutsideDiameterMm: 310.642,
    pipeWallThicknessMm: 22,
    factoryCoatingThicknessMm: 3,
    sideACutbackMm: 181,
    sideBCutbackMm: 266,
  };
  const heating = {
    ambientTemperatureC: 20,
    targetHeatTemperatureC: 231.4,
  };

  try {
    const raiaCoilFrames = activeStandardEquipmentCatalogue.coilFrames?.filter(
      (frame) => frame.id === "frame-12-75-hss",
    );
    const raiaBottomEnds = activeStandardEquipmentCatalogue.bottomEnds?.filter(
      (bottomEnd) => bottomEnd.id === "bottom-end-v8-16-contact-860",
    );

    const recommendation = recommendSetup({
      selectedPowerSourceId,
      standardEquipmentCatalogue: {
        powerSources: activeStandardEquipmentCatalogue.powerSources,
        ...(raiaCoilFrames === undefined ? {} : { coilFrames: raiaCoilFrames }),
        ...(raiaBottomEnds === undefined ? {} : { bottomEnds: raiaBottomEnds }),
      },
      recommendationOptions: {
        coilClearanceMm: 29,
        requiredOverhangMm: 50,
        tieBarDiameterMm: 0,
      },
      fieldJointGeometry,
      heating,
      applicationProduct: {
        id: "fbe-application",
        name: "FBE application",
      },
    });

    return {
      id: "config-raia-pvt1",
      reference: "RAIA PVT(1) 12.75 HSS / V8",
      project: "8792 RAIA",
      selectedPowerSourceId,
      fieldJointGeometry,
      heating,
      applicationProduct: {
        id: "fbe-application",
        name: "FBE application",
      },
      recommendation,
      createdAt: new Date("2026-06-15T12:00:00Z"),
    };
  } catch {
    return null;
  }
}

function initializeStubConfigurations(): void {
  if (savedCoilConfigurations.length > 0) {
    return;
  }

  const stubInputs: Array<{ ref: string; project: string; psId: string; odMm: number; wtMm: number; ctMm: number; saC: number; sbC: number; ambC: number; tgtC: number; sprocC?: number; prodName?: string }> = [
    { ref: "LK-001 Pipeline Section A", project: "Langkawi Pipeline", psId: "tesi-chg-180-50hz", odMm: 610, wtMm: 12.7, ctMm: 4, saC: 175, sbC: 175, ambC: 20, tgtC: 120, sprocC: 80, prodName: "Field joint coating" },
    { ref: "LK-002 Pipeline Section B", project: "Langkawi Pipeline", psId: "tesi-chg-180-50hz", odMm: 610, wtMm: 12.7, ctMm: 4, saC: 175, sbC: 175, ambC: 15, tgtC: 90 },
    { ref: "RJ-001 Riser Joint", project: "Riser Installation", psId: "tesi-chg-110", odMm: 273, wtMm: 9.3, ctMm: 3, saC: 100, sbC: 100, ambC: 25, tgtC: 150 },
  ];

  const baseDate = new Date("2026-06-12T09:00:00Z");

  for (const [index, stub] of stubInputs.entries()) {
    try {
      const input: RecommendSetupInput = {
        selectedPowerSourceId: stub.psId,
        fieldJointGeometry: {
          pipeOutsideDiameterMm: stub.odMm,
          pipeWallThicknessMm: stub.wtMm,
          factoryCoatingThicknessMm: stub.ctMm,
          sideACutbackMm: stub.saC,
          sideBCutbackMm: stub.sbC,
        },
        heating: {
          ambientTemperatureC: stub.ambC,
          targetHeatTemperatureC: stub.tgtC,
        },
        ...(stub.prodName !== undefined && stub.sprocC !== undefined
          ? {
              applicationProduct: {
                id: slugify(stub.prodName),
                name: stub.prodName,
                subsequentProcessTemperatureC: stub.sprocC,
              },
            }
          : {}),
      };

      const recommendation = recommendSetup(input);
      const createdAt = new Date(baseDate.getTime() + index * 2 * 60 * 60 * 1000);

      savedCoilConfigurations.push({
        id: `config-stub-${index + 1}`,
        reference: stub.ref,
        project: stub.project,
        selectedPowerSourceId: stub.psId,
        fieldJointGeometry: {
          pipeOutsideDiameterMm: stub.odMm,
          pipeWallThicknessMm: stub.wtMm,
          factoryCoatingThicknessMm: stub.ctMm,
          sideACutbackMm: stub.saC,
          sideBCutbackMm: stub.sbC,
        },
        heating: {
          ambientTemperatureC: stub.ambC,
          targetHeatTemperatureC: stub.tgtC,
        },
        ...(stub.prodName !== undefined && stub.sprocC !== undefined
          ? {
              applicationProduct: {
                id: slugify(stub.prodName),
                name: stub.prodName,
                subsequentProcessTemperatureC: stub.sprocC,
              },
            }
          : {}),
        recommendation,
        createdAt,
      });
    } catch {
      // Skip stubs that fail due to catalogue mismatch
    }
  }

  const raiaConfig = createRaiaWorkbookConfiguration();
  if (
    raiaConfig !== null &&
    !savedCoilConfigurations.some((config) => config.id === raiaConfig.id)
  ) {
    savedCoilConfigurations.push(raiaConfig);
  }
}

function initializeSeedMeasurementRecords(): void {
  if (savedMeasurementRecords.length > 0) {
    return;
  }

  const seedSpecs: Array<{
    configIndex: number;
    loggedAt: string;
    cycleRatio: number;
    deliveredPowerRatio: number;
    measuredTemperatureC: number;
    inFit: boolean;
  }> = [
    {
      configIndex: 0,
      loggedAt: "2026-06-13T10:00:00Z",
      cycleRatio: 1.18,
      deliveredPowerRatio: 0.97,
      measuredTemperatureC: 118,
      inFit: true,
    },
    {
      configIndex: 1,
      loggedAt: "2026-06-12T11:00:00Z",
      cycleRatio: 1.04,
      deliveredPowerRatio: 0.99,
      measuredTemperatureC: 121,
      inFit: false,
    },
    {
      configIndex: 2,
      loggedAt: "2026-06-11T09:30:00Z",
      cycleRatio: 1.12,
      deliveredPowerRatio: 0.96,
      measuredTemperatureC: 119,
      inFit: false,
    },
    {
      configIndex: 0,
      loggedAt: "2026-06-09T15:45:00Z",
      cycleRatio: 1.22,
      deliveredPowerRatio: 0.95,
      measuredTemperatureC: 116,
      inFit: true,
    },
    {
      configIndex: 1,
      loggedAt: "2026-06-05T14:20:00Z",
      cycleRatio: 1.07,
      deliveredPowerRatio: 1.01,
      measuredTemperatureC: 120,
      inFit: true,
    },
    {
      configIndex: 2,
      loggedAt: "2026-06-02T08:10:00Z",
      cycleRatio: 1.05,
      deliveredPowerRatio: 0.98,
      measuredTemperatureC: 122,
      inFit: true,
    },
  ];

  const records: MeasurementRecord[] = [];

  for (const [index, spec] of seedSpecs.entries()) {
    const config = savedCoilConfigurations[spec.configIndex];
    if (config === undefined) {
      continue;
    }

    const predictedCycle = config.recommendation.heatingCycleTimeMinutes;
    const predictedPower = config.recommendation.deliveredPowerKw;
    if (predictedCycle === null || predictedPower === null) {
      continue;
    }

    const measuredDeliveredPowerKw = predictedPower * spec.deliveredPowerRatio;
    const voltage = 400;
    const powerFactor = 0.86;
    const current = measuredDeliveredPowerKw * 1000 / (voltage * powerFactor);
    const powerSource = powerSourceForConfiguration(config);

    records.push({
      id: `seed-measurement-${index + 1}`,
      configurationId: config.id,
      configurationReference: config.reference,
      powerSourceLabel:
        powerSource === undefined
          ? config.selectedPowerSourceId
          : formatPowerSourceShortName(powerSource.name),
      ...(powerSource === undefined
        ? {}
        : { powerSourceFrequencyHz: powerSource.powerSourceFrequencyHz }),
      powerSourceVoltageV: voltage,
      powerSourceCurrentA: Math.round(current * 10) / 10,
      powerFactor,
      measuredTemperatureC: spec.measuredTemperatureC,
      measuredHeatingCycleTimeMinutes:
        Math.round(predictedCycle * spec.cycleRatio * 10) / 10,
      predictedHeatingCycleTimeMinutes: predictedCycle,
      predictedDeliveredPowerKw: predictedPower,
      measuredDeliveredPowerKw,
      notes: "Seed measurement for calibration workflow testing.",
      inFit: spec.inFit,
      loggedAt: new Date(spec.loggedAt),
    });
  }

  const raiaConfig = savedCoilConfigurations.find(
    (config) => config.id === "config-raia-pvt1",
  );

  records.push({
    id: "workbook-raia-pvt1-heat-profile",
    configurationId: raiaConfig?.id ?? "config-raia-pvt1",
    configurationReference:
      raiaConfig?.reference ?? "RAIA PVT(1) 12.75 HSS / V8",
    powerSourceLabel: "Tesi Deutz 120 kW",
    powerSourceFrequencyHz: 440,
    powerSourceVoltageV: 130,
    powerSourceCurrentA: 401,
    powerFactor: 0.985,
    measuredTemperatureC: 231.4,
    predictedHeatingCycleTimeMinutes:
      raiaConfig?.recommendation.heatingCycleTimeMinutes ?? null,
    predictedDeliveredPowerKw: raiaConfig?.recommendation.deliveredPowerKw ?? null,
    measuredDeliveredPowerKw: 51,
    notes:
      "Extracted from workbook worksheet 'Raia PQTPVT Settings': OD 310.642 mm, WT 22 mm, coating 3 mm, cutbacks 181/266 mm, 12.75 inch HSS frame with V8 860 mm 16-contact bottom end. Temperature readings were 228-240 °C across twelve positions, averaged to 231.4 °C for the single measured-temperature field. Cycle time was not present in the worksheet.",
    inFit: true,
    loggedAt: new Date("2026-06-15T12:00:00Z"),
  });

  savedMeasurementRecords = records;
}

export function convertToMm(value: number, unit: GeometryUnit | CoatingUnit): number {
  if (unit === "inch") {
    return value * 25.4;
  }

  if (unit === "um") {
    return value * 0.001;
  }

  return value;
}

export function convertFromMm(valueMm: number, unit: GeometryUnit | CoatingUnit): number {
  if (unit === "inch") {
    return valueMm / 25.4;
  }

  if (unit === "um") {
    return valueMm * 1000;
  }

  return valueMm;
}

export function buildConfigurationModalErrors(
  values: ConfigurationModalFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (values.configReference.trim() === "") {
    errors["configReference"] = "Reference is required.";
  }

  if (values.modalPipeOutsideDiameter.trim() === "" || !Number.isFinite(Number(values.modalPipeOutsideDiameter))) {
    errors["modalPipeOutsideDiameter"] = "Pipe Outside Diameter is required.";
  }

  if (values.modalPipeWallThickness.trim() === "" || !Number.isFinite(Number(values.modalPipeWallThickness))) {
    errors["modalPipeWallThickness"] = "Pipe Wall Thickness is required.";
  }

  if (values.modalFactoryCoatingThickness.trim() === "" || !Number.isFinite(Number(values.modalFactoryCoatingThickness))) {
    errors["modalFactoryCoatingThickness"] = "Factory Coating Thickness is required.";
  }

  if (values.modalSideACutback.trim() === "" || !Number.isFinite(Number(values.modalSideACutback))) {
    errors["modalSideACutback"] = "Side A Cutback is required.";
  }

  if (values.modalSideBCutback.trim() === "" || !Number.isFinite(Number(values.modalSideBCutback))) {
    errors["modalSideBCutback"] = "Side B Cutback is required.";
  }

  if (values.modalAmbientTemperatureC.trim() === "" || !Number.isFinite(Number(values.modalAmbientTemperatureC))) {
    errors["modalAmbientTemperatureC"] = "Ambient Temperature is required.";
  }

  if (values.modalTargetHeatTemperatureC.trim() === "" || !Number.isFinite(Number(values.modalTargetHeatTemperatureC))) {
    errors["modalTargetHeatTemperatureC"] = "Target Heat Temperature is required.";
  }

  return errors;
}

export function buildConfigurationModalInput(
  values: ConfigurationModalFormValues,
  catalogueInput?: StandardEquipmentCatalogue,
): RecommendSetupInput {
  const ambientTemperatureC = Number(values.modalAmbientTemperatureC);
  const startingTemperatureStr = values.modalStartingTemperatureC.trim();

  const input: RecommendSetupInput = {
    selectedPowerSourceId: values.modalSelectedPowerSourceId,
    fieldJointGeometry: {
      pipeOutsideDiameterMm: convertToMm(
        parseRequiredNumber(values.modalPipeOutsideDiameter, "Pipe Outside Diameter"),
        values.modalPipeOutsideDiameterUnit,
      ),
      pipeWallThicknessMm: convertToMm(
        parseRequiredNumber(values.modalPipeWallThickness, "Pipe Wall Thickness"),
        values.modalPipeWallThicknessUnit,
      ),
      factoryCoatingThicknessMm: convertToMm(
        parseRequiredNumber(values.modalFactoryCoatingThickness, "Factory Coating Thickness"),
        values.modalFactoryCoatingThicknessUnit,
      ),
      sideACutbackMm: convertToMm(
        parseRequiredNumber(values.modalSideACutback, "Side A Cutback"),
        values.modalSideACutbackUnit,
      ),
      sideBCutbackMm: convertToMm(
        parseRequiredNumber(values.modalSideBCutback, "Side B Cutback"),
        values.modalSideBCutbackUnit,
      ),
    },
    heating: {
      targetHeatTemperatureC: parseRequiredNumber(
        values.modalTargetHeatTemperatureC,
        "Target Heat Temperature",
      ),
      ...(Number.isFinite(ambientTemperatureC) ? { ambientTemperatureC } : {}),
      ...(startingTemperatureStr !== "" && Number.isFinite(Number(startingTemperatureStr))
        ? { startingTemperatureC: Number(startingTemperatureStr) }
        : {}),
    },
    ...(catalogueInput !== undefined ? { standardEquipmentCatalogue: catalogueInput } : {}),
  };

  const appProductName = values.modalApplicationProductName.trim();
  const subsequentTempStr = values.modalSubsequentProcessTemperatureC.trim();

  if (appProductName !== "") {
    input.applicationProduct = {
      id: slugify(appProductName),
      name: appProductName,
      ...(subsequentTempStr !== "" && Number.isFinite(Number(subsequentTempStr))
        ? { subsequentProcessTemperatureC: Number(subsequentTempStr) }
        : {}),
    };
  }

  return input;
}

export function filterConfigurations(
  configs: SavedCoilConfiguration[],
  search: string,
  statusFilter: string[],
): SavedCoilConfiguration[] {
  const normalizedSearch = search.trim().toLowerCase();
  const activeStatusFilter = statusFilter.length > 0 ? statusFilter : ["recommended", "marginal", "not supported"];

  return configs.filter((config) => {
    const matchesSearch =
      normalizedSearch === "" ||
      config.reference.toLowerCase().includes(normalizedSearch) ||
      config.project.toLowerCase().includes(normalizedSearch);

    const matchesStatus = activeStatusFilter.includes(
      config.recommendation.recommendationStatus,
    );

    return matchesSearch && matchesStatus;
  });
}

export function formatConfigurationDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = date.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year} ${month} ${day}`;
}

export function configurationStatusPillClass(status: string): string {
  if (status === "recommended") {
    return "status-pill status-pill--recommended";
  }

  if (status === "marginal") {
    return "status-pill status-pill--marginal";
  }

  return "status-pill status-pill--not-supported";
}

export function formatConfigurationStatusLabel(status: string): string {
  return status
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function detailStatusBannerClass(status: string): string {
  if (status === "recommended") {
    return "detail-status-banner detail-status-banner--recommended";
  }

  if (status === "marginal") {
    return "detail-status-banner detail-status-banner--marginal";
  }

  return "detail-status-banner detail-status-banner--not-supported";
}

export function utilizationFillClass(utilization: number | null): string {
  if (utilization === null) {
    return "";
  }

  const pct = utilization * 100;
  return pct >= 55 && pct <= 85
    ? "utilization-meter__fill--in-band"
    : "utilization-meter__fill--out-of-band";
}

export function buildDetailOperatingPointView(
  recommendation: RecommendedSetup,
): DetailOperatingPointView {
  const ps = recommendation.selectedPowerSource;
  const op = recommendation.operatingElectricalValues;
  const utilization = recommendation.powerUtilization;

  return {
    ratedLimits: {
      powerSourceRatingKva: String(ps.powerSourceRatingKva),
      powerSourceFrequencyHz: String(ps.powerSourceFrequencyHz),
      maximumPowerSourceVoltageV: String(ps.maximumPowerSourceVoltageV),
      maximumPowerSourceCurrentA: String(ps.maximumPowerSourceCurrentA),
    },
    operatingPoint: {
      powerSourceVoltageV: op === null ? "—" : String(op.powerSourceVoltageV),
      powerSourceCurrentA: op === null ? "—" : String(op.powerSourceCurrentA),
      powerFactor: op === null ? "—" : String(op.powerFactor),
      deliveredPowerKw:
        recommendation.deliveredPowerKw === null
          ? "—"
          : String(recommendation.deliveredPowerKw),
    },
    utilization: {
      displayValue: utilization === null ? "—" : formatPercent(utilization),
      fillPercent: utilization === null ? null : Math.round(utilization * 100),
      fillClass: utilizationFillClass(utilization),
    },
    timing: {
      heatingCycleTimeMinutes:
        recommendation.heatingCycleTimeMinutes === null
          ? "—"
          : String(recommendation.heatingCycleTimeMinutes),
      cooldownTimeMinutes:
        recommendation.cooldownTimeMinutes === undefined
          ? null
          : String(recommendation.cooldownTimeMinutes),
    },
  };
}

export interface DetailCoilComparisonView {
  optimum: {
    numberOfTurns: string;
    turnSpacingMm: string;
    coilCoverageMm: string;
    coilSectionMm: string;
    heatingLengthMm: string;
  };
  catalogueMatch: {
    numberOfTurns: string;
    turnSpacingMm: string;
    coilCoverageMm: string;
    coilSectionMm: string;
    heatingLengthMm: string;
  };
}

export interface DetailEquipmentCardView {
  powerSourceName: string;
  coilIdentifier: string;
  coilFrameSizeInches: string;
  effectiveCoilInsideDiameterMm: string;
  numberOfTurns: string;
  turnSpacingMm: string;
  coilCoverageMm: string;
  coilSectionMm: string;
  outputLimitingLabel: string;
  outputLimitingApplied: boolean;
}

export interface CoilLayoutSvgProps {
  sideACutbackMm: number;
  sideBCutbackMm: number;
  bareSteelLengthMm: number;
  coilCoverageMm: number;
  numberOfTurns: number;
  requiredOverhangMm: number;
  pipeOutsideDiameterMm: number;
}

export function buildDetailCoilComparisonView(
  recommendation: RecommendedSetup,
): DetailCoilComparisonView {
  const { optimum, catalogueMatch } = recommendation.coilRecommendationComparison;

  return {
    optimum: {
      numberOfTurns: String(optimum.numberOfTurns),
      turnSpacingMm: String(optimum.turnSpacingMm),
      coilCoverageMm: String(optimum.coilCoverageMm),
      coilSectionMm: "—",
      heatingLengthMm: "—",
    },
    catalogueMatch: {
      numberOfTurns: catalogueMatch !== null ? String(catalogueMatch.numberOfTurns) : "—",
      turnSpacingMm: catalogueMatch !== null ? String(catalogueMatch.turnSpacingMm) : "—",
      coilCoverageMm: catalogueMatch !== null ? String(catalogueMatch.coilCoverageMm) : "—",
      coilSectionMm:
        catalogueMatch !== null && catalogueMatch.coilSectionMm !== null
          ? String(catalogueMatch.coilSectionMm)
          : "—",
      heatingLengthMm:
        catalogueMatch !== null && catalogueMatch.heatingLengthMm !== null
          ? String(catalogueMatch.heatingLengthMm)
          : "—",
    },
  };
}

export function buildDetailEquipmentCardView(
  recommendation: RecommendedSetup,
): DetailEquipmentCardView {
  const coilConfig = recommendation.selectedCoilConfiguration;
  const ps = recommendation.selectedPowerSource;
  const op = recommendation.operatingElectricalValues;

  if (coilConfig === null) {
    return {
      powerSourceName: ps.name,
      coilIdentifier: "—",
      coilFrameSizeInches: "—",
      effectiveCoilInsideDiameterMm: "—",
      numberOfTurns: "—",
      turnSpacingMm: "—",
      coilCoverageMm: "—",
      coilSectionMm: "—",
      outputLimitingLabel: "—",
      outputLimitingApplied: false,
    };
  }

  const frameRef = coilConfig.coilFrame?.name ?? coilConfig.id;
  const bottomRef = coilConfig.bottomEnd?.name ?? "";
  const coilIdentifier = bottomRef !== "" ? `${frameRef} / ${bottomRef}` : frameRef;
  const outputLimitingApplied = op !== null && op.outputLimited;

  return {
    powerSourceName: ps.name,
    coilIdentifier,
    coilFrameSizeInches:
      coilConfig.coilFrameSizeInches !== undefined
        ? String(coilConfig.coilFrameSizeInches)
        : "—",
    effectiveCoilInsideDiameterMm:
      coilConfig.effectiveCoilInsideDiameterMm !== undefined
        ? String(coilConfig.effectiveCoilInsideDiameterMm)
        : "—",
    numberOfTurns: String(coilConfig.numberOfTurns),
    turnSpacingMm: String(coilConfig.turnSpacingMm),
    coilCoverageMm: String(coilConfig.coilCoverageMm),
    coilSectionMm:
      coilConfig.coilSectionMm !== undefined ? String(coilConfig.coilSectionMm) : "—",
    outputLimitingLabel: outputLimitingApplied ? "Applied" : "Not applied",
    outputLimitingApplied,
  };
}

export function buildCoilLayoutSvgProps(
  recommendation: RecommendedSetup,
  config: SavedCoilConfiguration,
): CoilLayoutSvgProps | null {
  const coilConfig = recommendation.selectedCoilConfiguration;

  if (coilConfig === null) {
    return null;
  }

  return {
    sideACutbackMm: config.fieldJointGeometry.sideACutbackMm,
    sideBCutbackMm: config.fieldJointGeometry.sideBCutbackMm,
    bareSteelLengthMm: recommendation.calculations.bareSteelLengthMm,
    coilCoverageMm: coilConfig.coilCoverageMm,
    numberOfTurns: coilConfig.numberOfTurns,
    requiredOverhangMm: recommendation.assumptions.requiredOverhangMm,
    pipeOutsideDiameterMm: config.fieldJointGeometry.pipeOutsideDiameterMm,
  };
}

export function createCoilLayoutSvg(props: CoilLayoutSvgProps): string {
  const W = 620;
  const H = 200;
  const marginLeft = 52;
  const marginRight = 40;
  const pipeTop = 82;
  const pipeHeight = 38;
  const pipeBottom = pipeTop + pipeHeight;
  const tickTop = 60;
  const bracketY = 18;

  const availWidth = W - marginLeft - marginRight;
  const totalSpanMm =
    props.sideACutbackMm + props.bareSteelLengthMm + props.sideBCutbackMm;
  const scale = totalSpanMm > 0 ? availWidth / totalSpanMm : 1;

  const xLeft = marginLeft;
  const xA = xLeft + props.sideACutbackMm * scale;
  const xB = xA + props.bareSteelLengthMm * scale;
  const xRight = xB + props.sideBCutbackMm * scale;
  const xCoilStart = xA - props.requiredOverhangMm * scale;
  const xCoilEnd = xB + props.requiredOverhangMm * scale;

  const r = (n: number): number => Math.round(n);
  const mx = (n: number): number => Math.max(0, Math.round(n));

  const rect = (
    x: number,
    y: number,
    w: number,
    h: number,
    fill: string,
    extra = "",
  ): string =>
    `<rect x="${r(x)}" y="${y}" width="${mx(w)}" height="${h}" fill="${fill}"${extra}/>`;

  const line = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    stroke: string,
    sw = "1",
    extra = "",
  ): string =>
    `<line x1="${r(x1)}" y1="${y1}" x2="${r(x2)}" y2="${y2}" stroke="${stroke}" stroke-width="${sw}"${extra}/>`;

  const text = (
    x: number,
    y: number,
    content: string,
    fontSize = "10",
    fill = "#374151",
    anchor = "middle",
  ): string =>
    `<text x="${r(x)}" y="${y}" font-size="${fontSize}" fill="${fill}" text-anchor="${anchor}" font-family="Public Sans, sans-serif">${content}</text>`;

  const parts: string[] = [];

  // Factory coating bands (grey, shaded)
  parts.push(rect(xLeft, pipeTop, xA - xLeft, pipeHeight, "#9ca3af"));
  parts.push(rect(xB, pipeTop, xRight - xB, pipeHeight, "#9ca3af"));

  // Bare steel band (lighter grey)
  parts.push(rect(xA, pipeTop, xB - xA, pipeHeight, "#d1d5db"));

  // Pipe outline
  parts.push(line(xLeft, pipeTop, xRight, pipeTop, "#6b7280", "1.5"));
  parts.push(line(xLeft, pipeBottom, xRight, pipeBottom, "#6b7280", "1.5"));
  parts.push(line(xLeft, pipeTop, xLeft, pipeBottom, "#6b7280", "1.5"));
  parts.push(line(xRight, pipeTop, xRight, pipeBottom, "#6b7280", "1.5"));

  // Turn tick marks
  const nTurns = props.numberOfTurns;
  if (nTurns > 0) {
    const spanPx = xCoilEnd - xCoilStart;
    const interval = nTurns > 1 ? spanPx / (nTurns - 1) : 0;
    for (let i = 0; i < nTurns; i++) {
      const xTick =
        nTurns === 1 ? (xCoilStart + xCoilEnd) / 2 : xCoilStart + i * interval;
      parts.push(
        `<line x1="${r(xTick)}" y1="${tickTop}" x2="${r(xTick)}" y2="${pipeTop}" stroke="#10b981" stroke-width="2" data-role="turn-tick"/>`,
      );
    }
  }

  // Coil coverage bracket above pipe
  parts.push(line(xCoilStart, bracketY + 12, xCoilEnd, bracketY + 12, "#10b981", "1.5"));
  parts.push(line(xCoilStart, bracketY + 12, xCoilStart, tickTop, "#10b981", "1.5"));
  parts.push(line(xCoilEnd, bracketY + 12, xCoilEnd, tickTop, "#10b981", "1.5"));
  parts.push(
    text(
      (xCoilStart + xCoilEnd) / 2,
      bracketY + 8,
      `Coil Coverage: ${props.coilCoverageMm} mm`,
      "9",
      "#10b981",
    ),
  );

  // Required overhang callouts
  parts.push(
    text(
      xCoilStart - 2,
      tickTop - 5,
      `← ${props.requiredOverhangMm} mm overhang`,
      "8",
      "#6b7280",
      "end",
    ),
  );
  parts.push(
    text(
      xCoilEnd + 2,
      tickTop - 5,
      `${props.requiredOverhangMm} mm overhang →`,
      "8",
      "#6b7280",
      "start",
    ),
  );

  // Bare steel label on pipe body
  if (xB - xA > 30) {
    parts.push(
      text((xA + xB) / 2, pipeTop + pipeHeight / 2 + 4, "Bare Steel", "9"),
    );
  }

  // Dimension labels below pipe
  const dimLineY = pipeBottom + 6;
  const dimTextY = pipeBottom + 20;

  // Bare steel length callout
  parts.push(line(xA, dimLineY, xA, dimTextY + 4, "#9ca3af"));
  parts.push(line(xB, dimLineY, xB, dimTextY + 4, "#9ca3af"));
  parts.push(line(xA + 2, dimLineY + 7, xB - 2, dimLineY + 7, "#9ca3af"));
  parts.push(
    text(
      (xA + xB) / 2,
      dimTextY + 18,
      `Bare Steel: ${props.bareSteelLengthMm} mm`,
      "9",
      "#6b7280",
    ),
  );

  // Side A cutback callout
  const cutbackY = dimLineY + 30;
  parts.push(line(xLeft, dimLineY, xLeft, cutbackY + 2, "#9ca3af"));
  parts.push(line(xA, dimLineY, xA, cutbackY + 2, "#9ca3af"));
  parts.push(line(xLeft + 2, cutbackY, xA - 2, cutbackY, "#9ca3af"));
  parts.push(
    text(
      (xLeft + xA) / 2,
      cutbackY + 12,
      `Side A: ${props.sideACutbackMm} mm`,
      "9",
      "#6b7280",
    ),
  );

  // Side B cutback callout
  parts.push(line(xB, dimLineY, xB, cutbackY + 2, "#9ca3af"));
  parts.push(line(xRight, dimLineY, xRight, cutbackY + 2, "#9ca3af"));
  parts.push(line(xB + 2, cutbackY, xRight - 2, cutbackY, "#9ca3af"));
  parts.push(
    text(
      (xB + xRight) / 2,
      cutbackY + 12,
      `Side B: ${props.sideBCutbackMm} mm`,
      "9",
      "#6b7280",
    ),
  );

  // Pipe OD indicator on left margin
  const oDx = xLeft - 10;
  parts.push(line(oDx, pipeTop, oDx, pipeBottom, "#6b7280"));
  parts.push(line(oDx - 3, pipeTop, oDx + 3, pipeTop, "#6b7280"));
  parts.push(line(oDx - 3, pipeBottom, oDx + 3, pipeBottom, "#6b7280"));
  parts.push(
    text(
      oDx - 5,
      (pipeTop + pipeBottom) / 2 + 4,
      `Ø${props.pipeOutsideDiameterMm}`,
      "8",
      "#6b7280",
      "end",
    ),
  );

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" role="img" aria-label="Coil layout schematic" xmlns="http://www.w3.org/2000/svg">${parts.join("")}</svg>`;
}

export interface DetailCalculatedBasisView {
  factoryCoatingOutsideDiameterMm: string;
  bareSteelLengthMm: string;
  heatingLengthMm: string;
  targetHeatTemperatureC: string;
  frequencyHeatingFactor: string;
}

export interface DetailCatalogueEvaluationView {
  standardConfigurationsGenerated: string;
  configurationsThatFit: string;
  configurationSelected: string;
  customCoilDesignReviewAdvised: string;
}

export function buildDetailCalculatedBasisView(
  recommendation: RecommendedSetup,
): DetailCalculatedBasisView {
  const { calculations, assumptions } = recommendation;

  return {
    factoryCoatingOutsideDiameterMm: calculations.factoryCoatingOutsideDiameterMm.toFixed(1),
    bareSteelLengthMm: calculations.bareSteelLengthMm.toFixed(1),
    heatingLengthMm:
      calculations.heatingLengthMm !== null
        ? calculations.heatingLengthMm.toFixed(1)
        : "—",
    targetHeatTemperatureC: assumptions.heatingCycleEstimate.targetHeatTemperatureC.toFixed(1),
    frequencyHeatingFactor: assumptions.heatingCycleEstimate.frequencyFactor.toFixed(4),
  };
}

export function buildDetailCatalogueEvaluationView(
  recommendation: RecommendedSetup,
): DetailCatalogueEvaluationView {
  const { catalogueEvaluation, selectedCoilConfiguration, advisoryActions } = recommendation;

  const customCoilReviewAdvised = advisoryActions.some(
    (a) => a.code === "custom-coil-design-review",
  );

  return {
    standardConfigurationsGenerated: String(catalogueEvaluation.standardCoilConfigurationCount),
    configurationsThatFit: String(catalogueEvaluation.fittingCoilConfigurationCount),
    configurationSelected: selectedCoilConfiguration !== null ? selectedCoilConfiguration.id : "—",
    customCoilDesignReviewAdvised: customCoilReviewAdvised ? "Yes" : "No",
  };
}

export function buildMeasurementLogErrors(
  values: MeasurementLogFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (values.configurationId === "") {
    errors["log-config"] = "Configuration is required.";
  }

  if (values.powerSourceVoltageV.trim() === "" || !Number.isFinite(Number(values.powerSourceVoltageV))) {
    errors["log-voltage"] = "Power Source Voltage is required.";
  }

  if (values.powerSourceCurrentA.trim() === "" || !Number.isFinite(Number(values.powerSourceCurrentA))) {
    errors["log-current"] = "Power Source Current is required.";
  }

  if (values.powerFactor.trim() === "" || !Number.isFinite(Number(values.powerFactor))) {
    errors["log-power-factor"] = "Power Factor is required.";
  }

  if (values.measuredTemperatureC.trim() === "" || !Number.isFinite(Number(values.measuredTemperatureC))) {
    errors["log-measured-temp"] = "Measured Temperature Reached is required.";
  }

  if (values.measuredHeatingCycleTimeMinutes.trim() === "" || !Number.isFinite(Number(values.measuredHeatingCycleTimeMinutes))) {
    errors["log-meas-cycle-time"] = "Measured Heating Cycle Time is required.";
  }

  return errors;
}

export function deltaCycleClass(
  predictedMinutes: number | null,
  measuredMinutes: number | undefined,
): string {
  if (
    predictedMinutes === null ||
    predictedMinutes === 0 ||
    measuredMinutes === undefined
  ) {
    return "";
  }

  const absDeltaPct = Math.abs(
    ((measuredMinutes - predictedMinutes) / predictedMinutes) * 100,
  );

  if (absDeltaPct <= 10) {
    return "delta--green";
  }

  if (absDeltaPct <= 20) {
    return "delta--amber";
  }

  return "delta--red";
}

export function buildMeasuredDataAggregateView(
  records: MeasurementRecord[],
): MeasuredDataAggregateView {
  const inFitRecords = records.filter((r) => r.inFit);

  const cycleRecords = inFitRecords.filter(
    (
      r,
    ): r is MeasurementRecord & {
      measuredHeatingCycleTimeMinutes: number;
      predictedHeatingCycleTimeMinutes: number;
    } =>
      r.measuredHeatingCycleTimeMinutes !== undefined &&
      r.predictedHeatingCycleTimeMinutes !== null &&
      r.predictedHeatingCycleTimeMinutes !== 0,
  );
  const meanCycleError =
    cycleRecords.length > 0
      ? cycleRecords.reduce((sum, r) => {
          const errorPercent =
            ((r.measuredHeatingCycleTimeMinutes - r.predictedHeatingCycleTimeMinutes) /
              r.predictedHeatingCycleTimeMinutes) *
            100;
          return sum + Math.abs(errorPercent);
        }, 0) / cycleRecords.length
      : null;

  const powerRecords = inFitRecords.filter(
    (r) => r.predictedDeliveredPowerKw !== null && r.predictedDeliveredPowerKw !== 0,
  );
  const meanPowerError =
    powerRecords.length > 0
      ? powerRecords.reduce((sum, r) => {
          const predicted = r.predictedDeliveredPowerKw as number;
          return sum + ((r.measuredDeliveredPowerKw - predicted) / predicted) * 100;
        }, 0) / powerRecords.length
      : null;

  const mostRecent =
    records.length > 0
      ? records.reduce((latest, r) => (r.loggedAt > latest.loggedAt ? r : latest))
      : null;

  return {
    recordCount: records.length,
    meanCycleTimeErrorPercent: meanCycleError !== null ? `${meanCycleError.toFixed(1)}%` : "—",
    meanDeliveredPowerErrorPercent: meanPowerError !== null ? `${meanPowerError.toFixed(1)}%` : "—",
    lastLoggedDate: mostRecent !== null ? formatConfigurationDate(mostRecent.loggedAt) : "—",
  };
}

const MATERIAL_CALIBRATION_THRESHOLD = 0.03;

export function resolveEditableCalibrationPointFrequency(
  selectedFrequencyHz: number,
  frequencyCalibrationCurve: FrequencyCalibrationPoint[],
): number {
  const sortedCurve = [...frequencyCalibrationCurve].sort(
    (left, right) => left.frequencyHz - right.frequencyHz,
  );
  const firstPoint = sortedCurve[0];
  const lastPoint = sortedCurve[sortedCurve.length - 1];

  if (firstPoint === undefined || lastPoint === undefined) {
    return selectedFrequencyHz;
  }

  const exactPoint = sortedCurve.find((point) => point.frequencyHz === selectedFrequencyHz);
  if (exactPoint !== undefined) {
    return exactPoint.frequencyHz;
  }

  if (selectedFrequencyHz <= firstPoint.frequencyHz) {
    return firstPoint.frequencyHz;
  }

  if (selectedFrequencyHz >= lastPoint.frequencyHz) {
    return lastPoint.frequencyHz;
  }

  for (let index = 0; index < sortedCurve.length - 1; index += 1) {
    const leftPoint = sortedCurve[index];
    const rightPoint = sortedCurve[index + 1];

    if (
      leftPoint !== undefined &&
      rightPoint !== undefined &&
      selectedFrequencyHz >= leftPoint.frequencyHz &&
      selectedFrequencyHz <= rightPoint.frequencyHz
    ) {
      return leftPoint.frequencyHz;
    }
  }

  return firstPoint.frequencyHz;
}

function tryPushHeatingCycleSuggestion(
  suggestions: CalibratedCoefficientSuggestion[],
  calibrationPointFrequencyHz: number,
  meanRatio: number,
  currentOptions: RecommendationOptions,
  materialThreshold: number,
): void {
  if (Math.abs(meanRatio - 1) <= materialThreshold) {
    return;
  }

  const refPoint = currentOptions.frequencyCalibrationCurve.find(
    (point) => point.frequencyHz === calibrationPointFrequencyHz,
  );
  if (refPoint === undefined) {
    return;
  }

  if (
    suggestions.some(
      (suggestion) =>
        suggestion.key === "heatingCycleFactor" &&
        suggestion.frequencyHz === calibrationPointFrequencyHz,
    )
  ) {
    return;
  }

  const raw = refPoint.heatingCycleFactor * meanRatio;
  const suggestedFactor = Math.round(Math.min(Math.max(raw, 0.5), 1.5) * 1000) / 1000;
  if (
    Math.abs(suggestedFactor - refPoint.heatingCycleFactor) <=
    materialThreshold * refPoint.heatingCycleFactor
  ) {
    return;
  }

  suggestions.push({
    key: "heatingCycleFactor",
    label: `Frequency heating factor (${calibrationPointFrequencyHz} Hz)`,
    current: refPoint.heatingCycleFactor,
    suggested: suggestedFactor,
    frequencyHz: calibrationPointFrequencyHz,
  });
}

export function deriveCalibrationSuggestions(
  inFitRecords: MeasurementRecord[],
  currentOptions: RecommendationOptions,
  materialThreshold = MATERIAL_CALIBRATION_THRESHOLD,
): CalibratedCoefficientSuggestion[] {
  if (inFitRecords.length < 2) {
    return [];
  }

  const suggestions: CalibratedCoefficientSuggestion[] = [];

  // Heating cycle time calibration: derive adjustment for the reference frequency heating cycle factor
  const heatingRecords = inFitRecords.filter(
    (
      r,
    ): r is MeasurementRecord & {
      measuredHeatingCycleTimeMinutes: number;
      predictedHeatingCycleTimeMinutes: number;
    } =>
      r.measuredHeatingCycleTimeMinutes !== undefined &&
      r.predictedHeatingCycleTimeMinutes !== null &&
      r.predictedHeatingCycleTimeMinutes > 0,
  );

  const heatingRecordsByCalibrationPoint = new Map<number, typeof heatingRecords>();
  for (const record of heatingRecords) {
    const measurementFrequencyHz =
      record.powerSourceFrequencyHz ?? currentOptions.referenceFrequencyHz;
    const calibrationPointFrequencyHz = resolveEditableCalibrationPointFrequency(
      measurementFrequencyHz,
      currentOptions.frequencyCalibrationCurve,
    );
    const recordsForPoint = heatingRecordsByCalibrationPoint.get(calibrationPointFrequencyHz) ?? [];
    recordsForPoint.push(record);
    heatingRecordsByCalibrationPoint.set(calibrationPointFrequencyHz, recordsForPoint);
  }

  for (const [calibrationPointFrequencyHz, recordsForPoint] of heatingRecordsByCalibrationPoint) {
    if (recordsForPoint.length < 2) {
      continue;
    }

    const meanRatio =
      recordsForPoint.reduce(
        (sum, record) =>
          sum + record.measuredHeatingCycleTimeMinutes / record.predictedHeatingCycleTimeMinutes,
        0,
      ) / recordsForPoint.length;

    tryPushHeatingCycleSuggestion(
      suggestions,
      calibrationPointFrequencyHz,
      meanRatio,
      currentOptions,
      materialThreshold,
    );
  }

  if (
    !suggestions.some((suggestion) => suggestion.key === "heatingCycleFactor") &&
    heatingRecords.length >= 2
  ) {
    const meanRatio =
      heatingRecords.reduce(
        (sum, record) =>
          sum + record.measuredHeatingCycleTimeMinutes / record.predictedHeatingCycleTimeMinutes,
        0,
      ) / heatingRecords.length;

    tryPushHeatingCycleSuggestion(
      suggestions,
      currentOptions.referenceFrequencyHz,
      meanRatio,
      currentOptions,
      materialThreshold,
    );
  }

  // Power utilization calibration: derive adjustment for powerUtilizationBase
  const powerRecords = inFitRecords.filter(
    (r): r is MeasurementRecord & { predictedDeliveredPowerKw: number } =>
      r.predictedDeliveredPowerKw !== null && r.predictedDeliveredPowerKw > 0,
  );

  if (powerRecords.length >= 2) {
    const meanPowerRatio =
      powerRecords.reduce(
        (sum, r) => sum + r.measuredDeliveredPowerKw / r.predictedDeliveredPowerKw,
        0,
      ) / powerRecords.length;

    if (Math.abs(meanPowerRatio - 1) > materialThreshold) {
      const currentBase = currentOptions.modelCoefficients.powerUtilizationBase;
      const rawBase = currentBase * meanPowerRatio;
      const suggestedBase = Math.round(Math.min(Math.max(rawBase, 0.1), 0.99) * 10000) / 10000;
      if (Math.abs(suggestedBase - currentBase) > materialThreshold * currentBase) {
        suggestions.push({
          key: "powerUtilizationBase",
          label: "Power utilization base",
          current: currentBase,
          suggested: suggestedBase,
        });
      }
    }
  }

  return suggestions;
}

export function buildCalibrationAdjustedRecommendationOptions(
  optionsInput: RecommendationOptionsInput,
  suggestions: CalibratedCoefficientSuggestion[],
): RecommendationOptionsInput {
  if (suggestions.length === 0) {
    return optionsInput;
  }

  const resolved = resolveDisplayRecommendationOptions(optionsInput);
  const curveUpdates = new Map<number, number>();
  const updatedModelCoefficients: Partial<RecommendationModelCoefficients> = {
    ...resolved.advancedCalibration.modelCoefficients,
  };

  for (const suggestion of suggestions) {
    if (suggestion.frequencyHz !== undefined) {
      curveUpdates.set(suggestion.frequencyHz, suggestion.suggested);
    } else {
      updatedModelCoefficients[suggestion.key as RecommendationModelCoefficientKey] =
        suggestion.suggested;
    }
  }

  const next: RecommendationOptionsInput = {
    ...optionsInput,
  };

  if (curveUpdates.size > 0) {
    next.frequencyCalibrationCurve = resolved.frequencyCalibrationCurve.map((point) =>
      curveUpdates.has(point.frequencyHz)
        ? { ...point, heatingCycleFactor: curveUpdates.get(point.frequencyHz)! }
        : point,
    );
  }

  if (suggestions.some((suggestion) => suggestion.frequencyHz === undefined)) {
    next.advancedCalibration = {
      ...optionsInput.advancedCalibration,
      modelCoefficients: updatedModelCoefficients,
    };
    next.modelCoefficients = updatedModelCoefficients;
  }

  return next;
}

export function openReviewCalibrationDialog(): void {
  if (typeof document === "undefined") {
    return;
  }

  const inFitRecords = savedMeasurementRecords.filter((r) => r.inFit);
  const currentOptions = resolveDisplayRecommendationOptions(activeRecommendationOptions);
  const suggestions = deriveCalibrationSuggestions(inFitRecords, currentOptions);

  const container = document.createElement("div");
  container.className = "calibration-review-body";

  const countParagraph = document.createElement("p");
  countParagraph.textContent =
    suggestions.length === 0
      ? `Based on ${inFitRecords.length} in-fit measurement record${inFitRecords.length === 1 ? "" : "s"}.`
      : `Based on ${inFitRecords.length} included measurements, these adjustments would reduce mean prediction error. Review before applying to the assumption set.`;
  container.append(countParagraph);

  if (suggestions.length === 0) {
    const noAdjustments = document.createElement("p");
    noAdjustments.className = "calibration-no-adjustments";
    noAdjustments.textContent = "No adjustments suggested based on current in-fit records.";
    container.append(noAdjustments);
  } else {
    const list = document.createElement("div");
    list.className = "calibration-suggestion-list";
    for (const suggestion of suggestions) {
      const row = document.createElement("div");
      row.className = "calibration-suggestion-row";

      const label = document.createElement("span");
      label.textContent = suggestion.label;

      const values = document.createElement("span");
      values.className = "calibration-suggestion-values mono-value";

      const current = document.createElement("span");
      current.className = "calibration-suggestion-current";
      current.textContent = String(suggestion.current);

      const arrow = document.createElement("span");
      arrow.className = "calibration-suggestion-arrow";
      arrow.textContent = "->";

      const suggested = document.createElement("strong");
      suggested.textContent = String(suggestion.suggested);

      values.append(current, arrow, suggested);
      row.append(label, values);
      list.append(row);
    }
    container.append(list);
  }

  showDialog(
    "Review suggested calibration",
    container,
    "Apply to assumptions",
    () => {
      if (suggestions.length > 0) {
        applyCalibrationSuggestions(suggestions);
      }
    },
    { disableCta: suggestions.length === 0 },
  );
}

function applyCalibrationSuggestions(suggestions: CalibratedCoefficientSuggestion[]): void {
  activeRecommendationOptions = buildCalibrationAdjustedRecommendationOptions(
    activeRecommendationOptions,
    suggestions,
  );

  const optForm = document.querySelector<HTMLFormElement>("#recommendation-options-form");
  if (optForm) {
    populateRecommendationOptionsForm(optForm, activeRecommendationOptions);
  }

  updateAssumptionsUnsavedState(
    hasUnsavedRecommendationAssumptionChanges(activeRecommendationOptions, savedRecommendationOptions),
  );

  const assumptionsTab = document.querySelector<HTMLButtonElement>(
    '[data-tab-target="assumptions-panel"]',
  );
  assumptionsTab?.click();
  window.scrollTo({ top: 0 });

  showToast("Calibration applied. Review and save assumptions to persist.");
}

function updateReviewCalibrationButtonState(): void {
  const button = document.querySelector<HTMLButtonElement>("#review-calibration-button");
  if (!button) {
    return;
  }
  const inFitCount = savedMeasurementRecords.filter((r) => r.inFit).length;
  button.disabled = inFitCount < 2;
}

function createMeasurementDialogForm(record?: MeasurementRecord): HTMLElement {
  const formContainer = document.createElement("div");
  formContainer.className = "add-catalogue-form";

  const configFieldEl = document.createElement("div");
  configFieldEl.className = "add-catalogue-field";

  const configLabelEl = document.createElement("label");
  configLabelEl.htmlFor = "log-config";
  configLabelEl.className = "add-catalogue-field__label";
  configLabelEl.textContent = "Configuration";
  configFieldEl.append(configLabelEl);

  const configSelect = document.createElement("select");
  configSelect.id = "log-config";
  configSelect.className = "add-catalogue-field__input";

  const defaultOption = document.createElement("option");
  defaultOption.value = "";
  defaultOption.textContent = "— Select a configuration —";
  configSelect.append(defaultOption);

  const existingConfigIds = new Set<string>();
  for (const config of savedCoilConfigurations) {
    const opt = document.createElement("option");
    opt.value = config.id;
    opt.textContent = config.reference;
    configSelect.append(opt);
    existingConfigIds.add(config.id);
  }

  if (record !== undefined && !existingConfigIds.has(record.configurationId)) {
    const opt = document.createElement("option");
    opt.value = record.configurationId;
    opt.textContent = `${record.configurationReference} (existing record)`;
    configSelect.append(opt);
  }

  configSelect.value = record?.configurationId ?? "";

  configFieldEl.append(configSelect);

  const configError = document.createElement("span");
  configError.id = "log-config-error";
  configError.className = "field-error";
  configError.hidden = true;
  configFieldEl.append(configError);
  formContainer.append(configFieldEl);

  const numericFields: Array<{ id: string; label: string; step: string }> = [
    { id: "log-voltage", label: "Power Source Voltage, V", step: "0.1" },
    { id: "log-current", label: "Power Source Current, A", step: "0.1" },
    { id: "log-power-factor", label: "Power Factor", step: "0.001" },
    { id: "log-measured-temp", label: "Measured Temperature Reached, °C", step: "0.1" },
    { id: "log-meas-cycle-time", label: "Measured Heating Cycle Time, min", step: "0.1" },
    { id: "log-meas-cooldown-time", label: "Measured Cooldown Time, min (optional)", step: "0.1" },
  ];

  for (const field of numericFields) {
    const fieldEl = createAddDialogField(field.id, field.label, "number", field.step);
    const input = fieldEl.querySelector<HTMLInputElement>("input");
    if (input !== null && record !== undefined) {
      const valueById: Record<string, string> = {
        "log-voltage": String(record.powerSourceVoltageV),
        "log-current": String(record.powerSourceCurrentA),
        "log-power-factor": String(record.powerFactor),
        "log-measured-temp": String(record.measuredTemperatureC),
        "log-meas-cycle-time":
          record.measuredHeatingCycleTimeMinutes === undefined
            ? ""
            : String(record.measuredHeatingCycleTimeMinutes),
        "log-meas-cooldown-time":
          record.measuredCooldownTimeMinutes === undefined
            ? ""
            : String(record.measuredCooldownTimeMinutes),
      };
      input.value = valueById[field.id] ?? "";
    }
    formContainer.append(fieldEl);
  }

  const notesFieldEl = document.createElement("div");
  notesFieldEl.className = "add-catalogue-field";

  const notesLabel = document.createElement("label");
  notesLabel.htmlFor = "log-notes";
  notesLabel.className = "add-catalogue-field__label";
  notesLabel.textContent = "Notes (optional)";
  notesFieldEl.append(notesLabel);

  const notesTextarea = document.createElement("textarea");
  notesTextarea.id = "log-notes";
  notesTextarea.className = "add-catalogue-field__input";
  notesTextarea.rows = 3;
  notesTextarea.value = record?.notes ?? "";
  notesFieldEl.append(notesTextarea);

  formContainer.append(notesFieldEl);

  return formContainer;
}

function readMeasurementDialogValues(): MeasurementLogFormValues {
  return {
    configurationId:
      (document.getElementById("log-config") as HTMLSelectElement | null)?.value ?? "",
    powerSourceVoltageV: getAddDialogValue("log-voltage"),
    powerSourceCurrentA: getAddDialogValue("log-current"),
    powerFactor: getAddDialogValue("log-power-factor"),
    measuredTemperatureC: getAddDialogValue("log-measured-temp"),
    measuredHeatingCycleTimeMinutes: getAddDialogValue("log-meas-cycle-time"),
    measuredCooldownTimeMinutes: getAddDialogValue("log-meas-cooldown-time"),
    notes: (document.getElementById("log-notes") as HTMLTextAreaElement | null)?.value ?? "",
  };
}

function validateMeasurementDialogValues(
  values: MeasurementLogFormValues,
  requireMeasuredCycleTime: boolean,
): Record<string, string> {
  const errors = buildMeasurementLogErrors(values);

  if (!requireMeasuredCycleTime) {
    delete errors["log-meas-cycle-time"];
  }

  return errors;
}

function showMeasurementDialogErrors(errors: Record<string, string>): void {
  showAddDialogErrors(
    [
      "log-config",
      "log-voltage",
      "log-current",
      "log-power-factor",
      "log-measured-temp",
      "log-meas-cycle-time",
    ],
    errors,
  );
}

function buildMeasurementRecordFromDialogValues(
  values: MeasurementLogFormValues,
  existingRecord?: MeasurementRecord,
): MeasurementRecord | null {
  const config = savedCoilConfigurations.find(
    (c) => c.id === values.configurationId,
  );

  if (config === undefined && existingRecord === undefined) {
    return null;
  }

  const voltage = Number(values.powerSourceVoltageV);
  const current = Number(values.powerSourceCurrentA);
  const pf = Number(values.powerFactor);
  const measuredCycle =
    values.measuredHeatingCycleTimeMinutes.trim() !== "" &&
    Number.isFinite(Number(values.measuredHeatingCycleTimeMinutes))
      ? Number(values.measuredHeatingCycleTimeMinutes)
      : undefined;
  const measuredCooldown =
    values.measuredCooldownTimeMinutes.trim() !== "" &&
    Number.isFinite(Number(values.measuredCooldownTimeMinutes))
      ? Number(values.measuredCooldownTimeMinutes)
      : undefined;

  const powerSource =
    config === undefined ? undefined : powerSourceForConfiguration(config);
  const powerSourceLabel =
    config === undefined
      ? existingRecord?.powerSourceLabel
      : powerSource === undefined
        ? config.selectedPowerSourceId
        : formatPowerSourceShortName(powerSource.name);

  return {
    id: existingRecord?.id ?? `meas-${Date.now()}`,
    configurationId: config?.id ?? existingRecord?.configurationId ?? values.configurationId,
    configurationReference:
      config?.reference ?? existingRecord?.configurationReference ?? values.configurationId,
    ...(powerSourceLabel === undefined ? {} : { powerSourceLabel }),
    ...(config === undefined
      ? existingRecord?.powerSourceFrequencyHz === undefined
        ? {}
        : { powerSourceFrequencyHz: existingRecord.powerSourceFrequencyHz }
      : powerSource === undefined
        ? {}
        : { powerSourceFrequencyHz: powerSource.powerSourceFrequencyHz }),
    powerSourceVoltageV: voltage,
    powerSourceCurrentA: current,
    powerFactor: pf,
    measuredTemperatureC: Number(values.measuredTemperatureC),
    ...(measuredCycle !== undefined
      ? { measuredHeatingCycleTimeMinutes: measuredCycle }
      : {}),
    ...(measuredCooldown !== undefined
      ? { measuredCooldownTimeMinutes: measuredCooldown }
      : {}),
    predictedHeatingCycleTimeMinutes:
      config?.recommendation.heatingCycleTimeMinutes ??
      existingRecord?.predictedHeatingCycleTimeMinutes ??
      null,
    predictedDeliveredPowerKw:
      config?.recommendation.deliveredPowerKw ??
      existingRecord?.predictedDeliveredPowerKw ??
      null,
    measuredDeliveredPowerKw: (voltage * current * pf) / 1000,
    notes: values.notes,
    inFit: existingRecord?.inFit ?? true,
    loggedAt: existingRecord?.loggedAt ?? new Date(),
  };
}

export function openLogMeasurementDialog(): void {
  if (typeof document === "undefined") {
    return;
  }

  const formContainer = createMeasurementDialogForm();

  showDialog("Log Measurement", formContainer, "Log Measurement", () => {
    const values = readMeasurementDialogValues();
    const errors = validateMeasurementDialogValues(values, true);
    showMeasurementDialogErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const record = buildMeasurementRecordFromDialogValues(values);
    if (record === null) {
      return false;
    }

    savedMeasurementRecords.push(record);
    renderMeasurementRows();
    updateMeasuredDataStatCards();
    persistApplicationStateAfterMutation("Measurement logged and saved to host");
  });
}

function openEditMeasurementDialog(recordId: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const record = savedMeasurementRecords.find((r) => r.id === recordId);
  if (record === undefined) {
    return;
  }

  const formContainer = createMeasurementDialogForm(record);

  showDialog("Edit Measurement", formContainer, "Save Measurement", () => {
    const values = readMeasurementDialogValues();
    const errors = validateMeasurementDialogValues(values, false);
    showMeasurementDialogErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const updatedRecord = buildMeasurementRecordFromDialogValues(values, record);
    if (updatedRecord === null) {
      return false;
    }

    const index = savedMeasurementRecords.findIndex((r) => r.id === record.id);
    if (index !== -1) {
      savedMeasurementRecords[index] = updatedRecord;
    }

    renderMeasurementRows();
    updateMeasuredDataStatCards();
    persistApplicationStateAfterMutation("Measurement updated and saved to host");
  });
}

function openDeleteMeasurementDialog(recordId: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const record = savedMeasurementRecords.find((r) => r.id === recordId);
  if (record === undefined) {
    return;
  }

  const body = document.createElement("p");
  body.textContent = `Delete measurement for ${record.configurationReference}? This cannot be undone.`;

  showDialog(
    "Delete Measurement",
    body,
    "Delete Measurement",
    () => {
      savedMeasurementRecords = savedMeasurementRecords.filter(
        (r) => r.id !== record.id,
      );
      renderMeasurementRows();
      updateMeasuredDataStatCards();
      persistApplicationStateAfterMutation("Measurement deleted and saved to host");
    },
  );
}

export function openAddPowerSourceDialog(catalogueForm?: HTMLFormElement): void {
  if (typeof document === "undefined") {
    return;
  }

  const form = document.createElement("div");
  form.className = "add-catalogue-form";

  const fields: Array<{ id: string; label: string; type: "text" | "number"; step?: string }> = [
    { id: "add-ps-id", label: "ID", type: "text" },
    { id: "add-ps-name", label: "Name", type: "text" },
    { id: "add-ps-rating", label: "Rating, kVA", type: "number", step: "0.1" },
    { id: "add-ps-frequency", label: "Frequency, Hz", type: "number", step: "0.1" },
    { id: "add-ps-voltage", label: "Max Voltage, V", type: "number", step: "0.1" },
    { id: "add-ps-current", label: "Max Current, A", type: "number", step: "0.1" },
  ];

  for (const field of fields) {
    const fieldEl = createAddDialogField(field.id, field.label, field.type, field.step);
    form.append(fieldEl);
  }

  showDialog("Add Power Source", form, "Add Power Source", () => {
    const id = getAddDialogValue("add-ps-id").trim();
    const name = getAddDialogValue("add-ps-name").trim();
    const rating = getAddDialogValue("add-ps-rating");
    const frequency = getAddDialogValue("add-ps-frequency");
    const voltage = getAddDialogValue("add-ps-voltage");
    const current = getAddDialogValue("add-ps-current");

    const errors: Record<string, string> = {};
    if (id === "") errors["add-ps-id"] = "ID is required.";
    if (name === "") errors["add-ps-name"] = "Name is required.";
    if (!isPositiveFiniteNumber(rating)) errors["add-ps-rating"] = "Rating must be a positive number.";
    if (!isPositiveFiniteNumber(frequency)) errors["add-ps-frequency"] = "Frequency must be a positive number.";
    if (!isPositiveFiniteNumber(voltage)) errors["add-ps-voltage"] = "Max Voltage must be a positive number.";
    if (!isPositiveFiniteNumber(current)) errors["add-ps-current"] = "Max Current must be a positive number.";

    showAddDialogErrors(["add-ps-id", "add-ps-name", "add-ps-rating", "add-ps-frequency", "add-ps-voltage", "add-ps-current"], errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const values: CataloguePowerSourceFormValues = {
      id,
      name,
      powerSourceRatingKva: rating,
      powerSourceFrequencyHz: frequency,
      maximumPowerSourceVoltageV: voltage,
      maximumPowerSourceCurrentA: current,
      minimumPowerUtilization: "0.55",
      maximumPowerUtilization: "0.85",
    };

    const targetForm = catalogueForm ?? document.querySelector<HTMLFormElement>("#standard-equipment-catalogue-form");
    if (targetForm !== null) {
      const tbody = getRequiredTableSection(targetForm, "#catalogue-power-sources");
      tbody.append(powerSourceTableRow(values));
      refreshCatalogueDraftState(targetForm);
    }
  });
}

export function openAddCoilFrameDialog(catalogueForm?: HTMLFormElement): void {
  if (typeof document === "undefined") {
    return;
  }

  const form = document.createElement("div");
  form.className = "add-catalogue-form";

  const fields: Array<{ id: string; label: string; type: "text" | "number"; step?: string }> = [
    { id: "add-cf-id", label: "ID", type: "text" },
    { id: "add-cf-name", label: "Name", type: "text" },
    { id: "add-cf-size", label: "Frame Size, in", type: "number", step: "0.5" },
    { id: "add-cf-diameter", label: "Inside Diameter, mm", type: "number", step: "0.1" },
  ];

  for (const field of fields) {
    form.append(createAddDialogField(field.id, field.label, field.type, field.step));
  }

  showDialog("Add Coil Frame", form, "Add Coil Frame", () => {
    const id = getAddDialogValue("add-cf-id").trim();
    const name = getAddDialogValue("add-cf-name").trim();
    const size = getAddDialogValue("add-cf-size");
    const diameter = getAddDialogValue("add-cf-diameter");

    const errors: Record<string, string> = {};
    if (id === "") errors["add-cf-id"] = "ID is required.";
    if (name === "") errors["add-cf-name"] = "Name is required.";
    if (!isPositiveFiniteNumber(size)) errors["add-cf-size"] = "Frame Size must be a positive number.";
    if (!isPositiveFiniteNumber(diameter)) errors["add-cf-diameter"] = "Inside Diameter must be a positive number.";

    showAddDialogErrors(["add-cf-id", "add-cf-name", "add-cf-size", "add-cf-diameter"], errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const values: CatalogueCoilFrameFormValues = {
      id,
      name,
      coilFrameSizeInches: size,
      coilFrameInsideDiameterMm: diameter,
    };

    const targetForm = catalogueForm ?? document.querySelector<HTMLFormElement>("#standard-equipment-catalogue-form");
    if (targetForm !== null) {
      const tbody = getRequiredTableSection(targetForm, "#catalogue-coil-frames");
      tbody.append(coilFrameTableRow(values));
      refreshCatalogueDraftState(targetForm);
    }
  });
}

export function openAddBottomEndDialog(catalogueForm?: HTMLFormElement): void {
  if (typeof document === "undefined") {
    return;
  }

  const form = document.createElement("div");
  form.className = "add-catalogue-form";

  const fields: Array<{ id: string; label: string; type: "text" | "number"; step?: string; hint?: string }> = [
    { id: "add-be-name", label: "Name", type: "text" },
    { id: "add-be-turns", label: "Turns", type: "number", step: "1" },
    { id: "add-be-spacing", label: "Contact Spacing mm (comma-separated, count = turns − 1)", type: "text", hint: "e.g. 75, 75, 75, 75, 75 for 6 turns" },
    { id: "add-be-width", label: "Bottom End Width, mm", type: "number", step: "0.1" },
  ];

  for (const field of fields) {
    form.append(createAddDialogField(field.id, field.label, field.type, field.step, field.hint));
  }

  showDialog("Add Bottom End", form, "Add Bottom End", () => {
    const name = getAddDialogValue("add-be-name").trim();
    const turnsStr = getAddDialogValue("add-be-turns");
    const spacingStr = getAddDialogValue("add-be-spacing");
    const widthStr = getAddDialogValue("add-be-width");

    const errors: Record<string, string> = {};
    if (name === "") errors["add-be-name"] = "Name is required.";

    let turns = 0;
    if (!isPositiveFiniteNumber(turnsStr) || !Number.isInteger(Number(turnsStr))) {
      errors["add-be-turns"] = "Turns must be a positive whole number.";
    } else {
      turns = Number(turnsStr);
    }

    if (!isPositiveFiniteNumber(widthStr)) {
      errors["add-be-width"] = "Bottom End Width must be a positive number.";
    }

    const spacingValues = spacingStr
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "");

    if (spacingValues.length === 0) {
      errors["add-be-spacing"] = "Contact Spacing values must be provided.";
    } else if (turns > 0 && spacingValues.length !== turns - 1) {
      errors["add-be-spacing"] = `Expected ${turns - 1} value${turns - 1 === 1 ? "" : "s"} for ${turns} turns.`;
    } else if (spacingValues.some((v) => !isPositiveFiniteNumber(v))) {
      errors["add-be-spacing"] = "All Contact Spacing values must be positive numbers.";
    }

    showAddDialogErrors(["add-be-name", "add-be-turns", "add-be-spacing", "add-be-width"], errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const id = slugify(name) || `bottom-end-${Date.now()}`;
    const values: CatalogueBottomEndFormValues = {
      id,
      name,
      numberOfTurns: turnsStr,
      firstContactEdgeOffsetMm: "52.5",
      lastContactEdgeOffsetMm: "52.5",
      contactCenterSpacingsMm: spacingStr,
      bottomEndWidthMm: widthStr,
      heatingResponseCelsiusMeterPerKwMinute: "0.35",
    };

    const targetForm = catalogueForm ?? document.querySelector<HTMLFormElement>("#standard-equipment-catalogue-form");
    if (targetForm !== null) {
      const tbody = getRequiredTableSection(targetForm, "#catalogue-bottom-ends");
      tbody.append(bottomEndTableRow(values));
      refreshCatalogueDraftState(targetForm);
    }
  });
}

function createAddDialogField(
  id: string,
  label: string,
  type: "text" | "number",
  step?: string,
  hint?: string,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "add-catalogue-field";

  const labelEl = document.createElement("label");
  labelEl.htmlFor = id;
  labelEl.className = "add-catalogue-field__label";
  labelEl.textContent = label;
  wrapper.append(labelEl);

  if (hint !== undefined) {
    const hintEl = document.createElement("span");
    hintEl.className = "add-catalogue-field__hint";
    hintEl.textContent = hint;
    wrapper.append(hintEl);
  }

  const input = document.createElement("input");
  input.id = id;
  input.type = type;
  input.className = "add-catalogue-field__input";
  if (step !== undefined) {
    input.step = step;
  }
  wrapper.append(input);

  const error = document.createElement("span");
  error.id = `${id}-error`;
  error.className = "field-error";
  error.hidden = true;
  wrapper.append(error);

  return wrapper;
}

function getAddDialogValue(id: string): string {
  const el = document.getElementById(id) as HTMLInputElement | null;
  return el?.value ?? "";
}

function showAddDialogErrors(fieldIds: string[], errors: Record<string, string>): void {
  for (const id of fieldIds) {
    const el = document.getElementById(`${id}-error`);
    if (el !== null) {
      const message = errors[id];
      if (message !== undefined) {
        el.textContent = message;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    }
  }
}

function isPositiveFiniteNumber(value: string): boolean {
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) && parsed > 0;
}

const HOSTED_STATE_ENDPOINT = "/api/coil-configurator/state";

if (typeof document !== "undefined" && document.querySelector("#recommendation-options-form")) {
  void initializeApplication();
}

export function buildRecommendationInput(
  values: RecommendationFormValues,
  recommendationOptions?: RecommendationOptionsInput,
  standardEquipmentCatalogueInput?: StandardEquipmentCatalogue,
): RecommendSetupInput {
  const applicationProductName = values.applicationProductName.trim();
  const subsequentProcessTemperatureC = parseOptionalNumber(
    values.subsequentProcessTemperatureC,
  );

  const input: RecommendSetupInput = {
    selectedPowerSourceId: values.selectedPowerSourceId,
    fieldJointGeometry: {
      pipeOutsideDiameterMm: parseRequiredNumber(
        values.pipeOutsideDiameterMm,
        "Pipe OD",
      ),
      pipeWallThicknessMm: parseRequiredNumber(
        values.pipeWallThicknessMm,
        "Wall thickness",
      ),
      factoryCoatingThicknessMm: parseRequiredNumber(
        values.factoryCoatingThicknessMm,
        "Factory coating",
      ),
      sideACutbackMm: parseRequiredNumber(values.sideACutbackMm, "Side A cutback"),
      sideBCutbackMm: parseRequiredNumber(values.sideBCutbackMm, "Side B cutback"),
    },
    heating: {
      targetHeatTemperatureC: parseRequiredNumber(
        values.targetHeatTemperatureC,
        "Target temperature",
      ),
      ...optionalNumberField("ambientTemperatureC", values.ambientTemperatureC),
      ...optionalNumberField("startingTemperatureC", values.startingTemperatureC),
    },
    ...(recommendationOptions === undefined
      ? {
          ...optionalNumberField("coilClearanceMm", values.coilClearanceMm),
          ...optionalNumberField("requiredOverhangMm", values.requiredOverhangMm),
        }
      : { recommendationOptions }),
    ...(standardEquipmentCatalogueInput === undefined
      ? {}
      : { standardEquipmentCatalogue: standardEquipmentCatalogueInput }),
  };

  if (applicationProductName !== "") {
    input.applicationProduct = {
      id: slugify(applicationProductName),
      name: applicationProductName,
      ...(subsequentProcessTemperatureC === undefined
        ? {}
        : { subsequentProcessTemperatureC }),
    };
  }

  return input;
}

export function buildRecommendationOptionsInput(
  values: RecommendationOptionsFormValues,
): RecommendationOptionsInput {
  const minimumTurns = parsePositiveInteger(values.minimumTurns, "Minimum turns");
  const maximumTurns = parsePositiveInteger(values.maximumTurns, "Maximum turns");

  if (minimumTurns > maximumTurns) {
    throw new Error("Minimum turns must be less than or equal to maximum turns.");
  }

  const modelCoefficients: Partial<RecommendationModelCoefficients> = {};
  for (const field of modelCoefficientFields) {
    modelCoefficients[field.key] = parseRequiredNumber(values[field.key], field.label);
  }

  const frequencyCalibrationCurve = values.frequencyCalibrationPoints.map(
    (point, index) => ({
      frequencyHz: parsePositiveNumber(
        point.frequencyHz,
        `Frequency calibration point ${index + 1} frequency`,
      ),
      heatingCycleFactor: parsePositiveNumber(
        point.heatingCycleFactor,
        `Frequency calibration point ${index + 1} heating factor`,
      ),
      cooldownFactor: parsePositiveNumber(
        point.cooldownFactor,
        `Frequency calibration point ${index + 1} cooldown factor`,
      ),
    }),
  );

  rejectNonIncreasingFrequencyCalibrationCurve(frequencyCalibrationCurve);

  return {
    referenceFrequencyHz: parsePositiveNumber(
      values.referenceFrequencyHz,
      "Reference frequency",
    ),
    preferredTurnSpacingMm: parsePositiveNumber(
      values.preferredTurnSpacingMm,
      "Preferred turn spacing",
    ),
    turnBounds: {
      minimumTurns,
      maximumTurns,
    },
    coilClearanceMm: parseNonNegativeNumber(values.coilClearanceMm, "Coil clearance"),
    requiredOverhangMm: parseNonNegativeNumber(
      values.requiredOverhangMm,
      "Required overhang",
    ),
    tieBarDiameterMm: parseNonNegativeNumber(
      values.tieBarDiameterMm,
      "Tie bar diameter",
    ),
    frequencyCalibrationCurve,
    advisoryThresholds: {
      lowPowerUtilization: parseUtilizationLimit(
        values.lowPowerUtilization,
        "Low power utilization threshold",
      ),
      minimumCoilFrameSizeMarginMm: parseNonNegativeNumber(
        values.minimumCoilFrameSizeMarginMm,
        "Fit margin threshold",
      ),
      minimumCoilCoverageMarginMm: parseNonNegativeNumber(
        values.minimumCoilCoverageMarginMm,
        "Coverage margin threshold",
      ),
      powerUtilizationBoundaryMargin: parseNonNegativeNumber(
        values.powerUtilizationBoundaryMargin,
        "Power utilization boundary margin",
      ),
    },
    advancedCalibration: {
      modelCoefficients,
    },
  };
}

export function buildStandardEquipmentCatalogueInput(
  values: StandardEquipmentCatalogueFormValues,
): StandardEquipmentCatalogue {
  const powerSources = values.powerSources.map((powerSource) => {
      const minimumPowerUtilization = parseUtilizationLimit(
        powerSource.minimumPowerUtilization,
        `${powerSource.name} minimum utilization`,
      );
      const maximumPowerUtilization = parseUtilizationLimit(
        powerSource.maximumPowerUtilization,
        `${powerSource.name} maximum utilization`,
      );

      if (minimumPowerUtilization > maximumPowerUtilization) {
        throw new Error(
          `${powerSource.name} minimum utilization must be less than or equal to maximum utilization.`,
        );
      }

      return {
        id: requireCatalogueText(powerSource.id, `${powerSource.name} id`),
        name: requireCatalogueText(powerSource.name, "Power source name"),
        powerSourceRatingKva: parsePositiveNumber(
          powerSource.powerSourceRatingKva,
          `${powerSource.name} rating`,
        ),
        powerSourceFrequencyHz: parsePositiveNumber(
          powerSource.powerSourceFrequencyHz,
          `${powerSource.name} frequency`,
        ),
        maximumPowerSourceVoltageV: parsePositiveNumber(
          powerSource.maximumPowerSourceVoltageV,
          `${powerSource.name} voltage`,
        ),
        maximumPowerSourceCurrentA: parsePositiveNumber(
          powerSource.maximumPowerSourceCurrentA,
          `${powerSource.name} current`,
        ),
        operatingLimits: {
          minimumPowerUtilization,
          maximumPowerUtilization,
        },
      };
    });
  const coilFrames = values.coilFrames.map((coilFrame) => ({
      id: requireCatalogueText(coilFrame.id, `${coilFrame.name} id`),
      name: requireCatalogueText(coilFrame.name, "Coil frame name"),
      coilFrameSizeInches: parsePositiveNumber(
        coilFrame.coilFrameSizeInches,
        `${coilFrame.name} frame size`,
      ),
      coilFrameInsideDiameterMm: parsePositiveNumber(
        coilFrame.coilFrameInsideDiameterMm,
        `${coilFrame.name} inside diameter`,
      ),
    }));
  const bottomEnds = values.bottomEnds.map((bottomEnd) => {
      const numberOfTurns = parsePositiveInteger(
        bottomEnd.numberOfTurns,
        `${bottomEnd.name} number of turns`,
      );
      const firstContactEdgeOffsetMm = parseNonNegativeNumber(
        bottomEnd.firstContactEdgeOffsetMm,
        `${bottomEnd.name} first contact edge offset`,
      );
      const lastContactEdgeOffsetMm = parseNonNegativeNumber(
        bottomEnd.lastContactEdgeOffsetMm,
        `${bottomEnd.name} last contact edge offset`,
      );
      const contactCenterSpacingsMm = parseContactCenterSpacings(
        bottomEnd.contactCenterSpacingsMm,
        `${bottomEnd.name} contact center spacing`,
      );
      const bottomEndWidthMm = parsePositiveNumber(
        bottomEnd.bottomEndWidthMm,
        `${bottomEnd.name} bottom end width`,
      );

      if (contactCenterSpacingsMm.length !== numberOfTurns - 1) {
        throw new Error(
          `${bottomEnd.name} contact center spacing count must equal number of turns minus 1.`,
        );
      }

      if (bottomEndWidthMm <= firstContactEdgeOffsetMm + lastContactEdgeOffsetMm) {
        throw new Error(
          `${bottomEnd.name} bottom end width must be greater than the first and last contact edge offsets.`,
        );
      }

      return {
        id: requireCatalogueText(bottomEnd.id, `${bottomEnd.name} id`),
        name: requireCatalogueText(bottomEnd.name, "Bottom end name"),
        numberOfTurns,
        firstContactEdgeOffsetMm,
        lastContactEdgeOffsetMm,
        contactCenterSpacingsMm,
        bottomEndWidthMm,
        heatingResponseCelsiusMeterPerKwMinute: parsePositiveNumber(
          bottomEnd.heatingResponseCelsiusMeterPerKwMinute,
          `${bottomEnd.name} heating response`,
        ),
      };
    });

  if (powerSources.length === 0) {
    throw new Error("At least one power source is required.");
  }

  if (coilFrames.length === 0) {
    throw new Error("At least one coil frame is required.");
  }

  if (bottomEnds.length === 0) {
    throw new Error("At least one bottom end is required.");
  }

  rejectDuplicateCatalogueIds(powerSources, "power source");
  rejectDuplicateCatalogueIds(coilFrames, "coil frame");
  rejectDuplicateCatalogueIds(bottomEnds, "bottom end");

  return {
    powerSources,
    coilFrames,
    bottomEnds,
  };
}

export function buildCatalogueMaintenancePreview(
  values: StandardEquipmentCatalogueFormValues,
): CatalogueMaintenancePreview {
  const catalogue = buildStandardEquipmentCatalogueInput(values);

  return {
    generatedCatalogueCount:
      (catalogue.coilFrames?.length ?? 0) * (catalogue.bottomEnds?.length ?? 0),
    bottomEnds: catalogue.bottomEnds?.map(calculateBottomEndDerivedValues) ?? [],
  };
}

export function addCataloguePowerSourceFormRow(
  values: StandardEquipmentCatalogueFormValues,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    powerSources: [
      ...values.powerSources,
      defaultPowerSourceFormValues(values.powerSources.length + 1),
    ],
  };
}

export function addCatalogueCoilFrameFormRow(
  values: StandardEquipmentCatalogueFormValues,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    coilFrames: [
      ...values.coilFrames,
      defaultCoilFrameFormValues(values.coilFrames.length + 1),
    ],
  };
}

export function addCatalogueBottomEndFormRow(
  values: StandardEquipmentCatalogueFormValues,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    bottomEnds: [
      ...values.bottomEnds,
      defaultBottomEndFormValues(values.bottomEnds.length + 1),
    ],
  };
}

export function removeCataloguePowerSourceFormRow(
  values: StandardEquipmentCatalogueFormValues,
  rowIndex: number,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    powerSources: removeFormRow(values.powerSources, rowIndex),
  };
}

export function removeCatalogueCoilFrameFormRow(
  values: StandardEquipmentCatalogueFormValues,
  rowIndex: number,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    coilFrames: removeFormRow(values.coilFrames, rowIndex),
  };
}

export function removeCatalogueBottomEndFormRow(
  values: StandardEquipmentCatalogueFormValues,
  rowIndex: number,
): StandardEquipmentCatalogueFormValues {
  return {
    ...values,
    bottomEnds: removeFormRow(values.bottomEnds, rowIndex),
  };
}

export function serializeStandardEquipmentCatalogue(
  catalogue: StandardEquipmentCatalogue,
): string {
  return `${JSON.stringify(catalogue, null, 2)}\n`;
}

export function serializeRecommendationAssumptions(
  assumptions: RecommendationOptionsInput,
): string {
  const options = resolveDisplayRecommendationOptions(assumptions);
  const persistedAssumptions = {
    referenceFrequencyHz: options.referenceFrequencyHz,
    preferredTurnSpacingMm: options.preferredTurnSpacingMm,
    turnBounds: {
      ...options.turnBounds,
    },
    coilClearanceMm: options.coilClearanceMm,
    requiredOverhangMm: options.requiredOverhangMm,
    tieBarDiameterMm: options.tieBarDiameterMm,
    frequencyCalibrationCurve: options.frequencyCalibrationCurve.map((point) => ({
      ...point,
    })),
    advisoryThresholds: {
      ...options.advisoryThresholds,
    },
    advancedCalibration: {
      modelCoefficients: {
        ...options.advancedCalibration.modelCoefficients,
      },
    },
  };

  return `${JSON.stringify(persistedAssumptions, null, 2)}\n`;
}

export function hasUnsavedCatalogueChanges(
  activeCatalogue: StandardEquipmentCatalogue,
  savedCatalogue: StandardEquipmentCatalogue,
): boolean {
  return (
    serializeStandardEquipmentCatalogue(activeCatalogue) !==
    serializeStandardEquipmentCatalogue(savedCatalogue)
  );
}

export function hasUnsavedRecommendationAssumptionChanges(
  activeAssumptions: RecommendationOptionsInput,
  savedAssumptions: RecommendationOptionsInput,
): boolean {
  return (
    serializeRecommendationAssumptions(activeAssumptions) !==
    serializeRecommendationAssumptions(savedAssumptions)
  );
}

async function initializeApplication(): Promise<void> {
  const optionsForm =
    document.querySelector<HTMLFormElement>("#recommendation-options-form");
  const catalogueForm =
    document.querySelector<HTMLFormElement>("#standard-equipment-catalogue-form");
  const saveCatalogueButton = document.querySelector<HTMLButtonElement>(
    "#save-catalogue-button",
  );
  const addPowerSourceButton = document.querySelector<HTMLButtonElement>(
    "#add-power-source-button",
  );
  const addCoilFrameButton = document.querySelector<HTMLButtonElement>(
    "#add-coil-frame-button",
  );
  const addBottomEndButton = document.querySelector<HTMLButtonElement>(
    "#add-bottom-end-button",
  );
  const saveAssumptionsButton = document.querySelector<HTMLButtonElement>(
    "#save-assumptions-button",
  );
  const addCalibrationPointButton = document.querySelector<HTMLButtonElement>(
    "#add-calibration-point-button",
  );

  if (
    !optionsForm ||
    !catalogueForm ||
    !saveCatalogueButton ||
    !addPowerSourceButton ||
    !addCoilFrameButton ||
    !addBottomEndButton ||
    !saveAssumptionsButton
  ) {
    throw new Error("The recommendation form could not be initialized.");
  }

  let hostedStateLoadError: string | null = null;
  try {
    await loadHostedApplicationState();
  } catch (error) {
    hostedStateLoadError = formatError(error);
    console.warn(hostedStateLoadError);
  }

  initializePageHeader();
  initializeDialog();
  initializeGlossaryDrawer();
  initializeInfoAffordances();
  initializeConfigurationsListView();
  initializeConfigurationModal();
  initializeDetailView();
  initializeMeasuredDataTab();

  optionsFeedback = getRequiredElement("#options-feedback");
  assumptionsUnsavedState = getRequiredElement("#assumptions-unsaved-state");
  catalogueFeedback = getRequiredElement("#catalogue-feedback");
  catalogueUnsavedState = getRequiredElement("#catalogue-unsaved-state");
  generatedCatalogueCount = getRequiredElement("#generated-catalogue-count");

  populateRecommendationOptionsForm(optionsForm, activeRecommendationOptions);
  populateStandardEquipmentCatalogueForm(
    catalogueForm,
    activeStandardEquipmentCatalogue,
  );
  updateCatalogueMaintenancePreview(catalogueForm);
  updateCatalogueUnsavedState(false);
  updateAssumptionsUnsavedState(false);
  if (hostedStateLoadError !== null) {
    showToast(`State load failed: ${hostedStateLoadError}`, 8000);
  }
  initializeTabs();

  optionsForm.addEventListener("submit", (event) => {
    event.preventDefault();
    try {
      activeRecommendationOptions = buildRecommendationOptionsInput(
        readRecommendationOptionsFormValues(optionsForm),
      );
      updateAssumptionsUnsavedState(
        hasUnsavedRecommendationAssumptionChanges(
          activeRecommendationOptions,
          savedRecommendationOptions,
        ),
      );
      optionsFeedback.hidden = false;
      optionsFeedback.className = "options-feedback";
      optionsFeedback.textContent = "Assumption edits previewed.";
    } catch (error) {
      optionsFeedback.hidden = false;
      optionsFeedback.className = "options-feedback options-feedback-error";
      optionsFeedback.textContent = formatError(error);
    }
  });

  optionsForm.addEventListener("input", () => {
    try {
      const draftAssumptions = buildRecommendationOptionsInput(
        readRecommendationOptionsFormValues(optionsForm),
      );
      activeRecommendationOptions = draftAssumptions;
      updateAssumptionsUnsavedState(
        hasUnsavedRecommendationAssumptionChanges(
          draftAssumptions,
          savedRecommendationOptions,
        ),
      );
      optionsFeedback.hidden = false;
      optionsFeedback.className = "options-feedback";
      optionsFeedback.textContent = "Assumption edits previewed.";
    } catch (error) {
      updateAssumptionsUnsavedState(true);
      optionsFeedback.hidden = false;
      optionsFeedback.className = "options-feedback options-feedback-error";
      optionsFeedback.textContent = formatError(error);
    }
  });

  catalogueForm.addEventListener("submit", (event) => {
    event.preventDefault();
    try {
      activeStandardEquipmentCatalogue = buildStandardEquipmentCatalogueInput(
        readStandardEquipmentCatalogueFormValues(catalogueForm),
      );
      updateCatalogueUnsavedState(
        hasUnsavedCatalogueChanges(
          activeStandardEquipmentCatalogue,
          savedStandardEquipmentCatalogue,
        ),
      );
      catalogueFeedback.hidden = false;
      catalogueFeedback.className = "options-feedback";
      catalogueFeedback.textContent = "Catalogue applied in memory.";
      updateCatalogueMaintenancePreview(catalogueForm);
    } catch (error) {
      catalogueFeedback.hidden = false;
      catalogueFeedback.className = "options-feedback options-feedback-error";
      catalogueFeedback.textContent = formatError(error);
    }
  });

  catalogueForm.addEventListener("input", () => {
    try {
      const draftCatalogue = buildStandardEquipmentCatalogueInput(
        readStandardEquipmentCatalogueFormValues(catalogueForm),
      );
      updateCatalogueUnsavedState(
        hasUnsavedCatalogueChanges(draftCatalogue, savedStandardEquipmentCatalogue),
      );
      updateCatalogueMaintenancePreview(catalogueForm);
      catalogueFeedback.hidden = true;
    } catch {
      updateCatalogueUnsavedState(true);
      updateCatalogueMaintenancePreview(catalogueForm);
      catalogueFeedback.hidden = false;
      catalogueFeedback.className = "options-feedback options-feedback-error";
      catalogueFeedback.textContent = "Catalogue edits contain invalid data.";
    }
  });

  addPowerSourceButton.addEventListener("click", () => {
    openAddPowerSourceDialog(catalogueForm);
  });

  addCoilFrameButton.addEventListener("click", () => {
    openAddCoilFrameDialog(catalogueForm);
  });

  addBottomEndButton.addEventListener("click", () => {
    openAddBottomEndDialog(catalogueForm);
  });

  catalogueForm.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const removeButton = target.closest<HTMLButtonElement>(
      "[data-remove-catalogue-row]",
    );
    if (removeButton === null) {
      return;
    }

    const row = removeButton.closest("tr");
    showDialog(
      "Remove Row",
      "Are you sure you want to remove this row? This cannot be undone.",
      "Remove",
      () => {
        row?.remove();
        void saveCatalogueFromForm(catalogueForm);
      },
    );
  });

  saveCatalogueButton.addEventListener("click", () => {
    void saveCatalogueFromForm(catalogueForm);
  });

  addCalibrationPointButton?.addEventListener("click", () => {
    addFrequencyCalibrationPoint(optionsForm);
  });

  optionsForm.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const removeButton = target.closest<HTMLButtonElement>(
      "[data-remove-calibration-row]",
    );

    if (removeButton === null) {
      return;
    }

    removeButton.closest("tr")?.remove();
    optionsForm.dispatchEvent(new Event("input"));
  });

  saveAssumptionsButton.addEventListener("click", () => {
    void saveAssumptionsFromForm(optionsForm);
  });

  window.addEventListener("beforeunload", (event) => {
    if (hasUnsavedCatalogueChanges(activeStandardEquipmentCatalogue, savedStandardEquipmentCatalogue)) {
      event.preventDefault();
      event.returnValue = "";
    }
  });
}

async function saveAssumptionsFromForm(
  optionsForm: HTMLFormElement,
): Promise<void> {
  try {
    activeRecommendationOptions = buildRecommendationOptionsInput(
      readRecommendationOptionsFormValues(optionsForm),
    );
    await saveHostedApplicationState();
    savedRecommendationOptions = copyRecommendationOptions(
      resolveDisplayRecommendationOptions(activeRecommendationOptions),
    );
    updateAssumptionsUnsavedState(false);
    optionsFeedback.hidden = false;
    optionsFeedback.className = "options-feedback";
    optionsFeedback.textContent = "Assumptions saved to hosted application state.";
  } catch (error) {
    optionsFeedback.hidden = false;
    optionsFeedback.className = "options-feedback options-feedback-error";
    optionsFeedback.textContent = formatError(error);
  }
}

async function saveCatalogueFromForm(
  catalogueForm: HTMLFormElement,
): Promise<void> {
  try {
    activeStandardEquipmentCatalogue = buildStandardEquipmentCatalogueInput(
      readStandardEquipmentCatalogueFormValues(catalogueForm),
    );
    await saveHostedApplicationState();
    savedStandardEquipmentCatalogue = copyStandardEquipmentCatalogue(
      activeStandardEquipmentCatalogue,
    );
    updateCatalogueUnsavedState(false);
    catalogueFeedback.hidden = false;
    catalogueFeedback.className = "options-feedback";
    catalogueFeedback.textContent = "Catalogue saved to hosted application state.";
    updateCatalogueMaintenancePreview(catalogueForm);
  } catch (error) {
    catalogueFeedback.hidden = false;
    catalogueFeedback.className = "options-feedback options-feedback-error";
    catalogueFeedback.textContent = formatError(error);
  }
}

function populatePowerSourceOptions(
  select: HTMLSelectElement,
  catalogue: StandardEquipmentCatalogue,
  preferredPowerSourceId = "ps-120",
): void {
  const selectedPowerSourceId = catalogue.powerSources.some(
    (powerSource) => powerSource.id === preferredPowerSourceId,
  )
    ? preferredPowerSourceId
    : catalogue.powerSources[0]?.id;

  select.replaceChildren(
    ...catalogue.powerSources.map((powerSource) => {
      const option = document.createElement("option");
      option.value = powerSource.id;
      option.textContent = `${powerSource.name} (${powerSource.powerSourceRatingKva} kVA)`;
      option.selected = powerSource.id === selectedPowerSourceId;
      return option;
    }),
  );
}

function readRecommendationOptionsFormValues(
  targetForm: HTMLFormElement,
): RecommendationOptionsFormValues {
  const formData = new FormData(targetForm);
  const values = {
    referenceFrequencyHz: getFormString(formData, "referenceFrequencyHz"),
    preferredTurnSpacingMm: getFormString(formData, "preferredTurnSpacingMm"),
    minimumTurns: getFormString(formData, "minimumTurns"),
    maximumTurns: getFormString(formData, "maximumTurns"),
    coilClearanceMm: getFormString(formData, "optionCoilClearanceMm"),
    requiredOverhangMm: getFormString(formData, "optionRequiredOverhangMm"),
    tieBarDiameterMm: getFormString(formData, "tieBarDiameterMm"),
    frequencyCalibrationPoints: zipFormRows([
      "frequencyCalibrationHz",
      "heatingCycleFactor",
      "cooldownFactor",
    ].map((key) => getFormStringList(formData, key))).map((row) => ({
      frequencyHz: row[0] ?? "",
      heatingCycleFactor: row[1] ?? "",
      cooldownFactor: row[2] ?? "",
    })),
    lowPowerUtilization: getFormString(formData, "lowPowerUtilization"),
    minimumCoilFrameSizeMarginMm: getFormString(
      formData,
      "minimumCoilFrameSizeMarginMm",
    ),
    minimumCoilCoverageMarginMm: getFormString(
      formData,
      "minimumCoilCoverageMarginMm",
    ),
    powerUtilizationBoundaryMargin: getFormString(
      formData,
      "powerUtilizationBoundaryMargin",
    ),
  } as RecommendationOptionsFormValues;

  for (const field of modelCoefficientFields) {
    values[field.key] = getFormString(formData, field.key);
  }

  return values;
}

function readStandardEquipmentCatalogueFormValues(
  targetForm: HTMLFormElement,
): StandardEquipmentCatalogueFormValues {
  const formData = new FormData(targetForm);

  return {
    powerSources: zipFormRows([
      "powerSourceId",
      "powerSourceName",
      "powerSourceRatingKva",
      "powerSourceFrequencyHz",
      "maximumPowerSourceVoltageV",
      "maximumPowerSourceCurrentA",
      "minimumPowerUtilization",
      "maximumPowerUtilization",
    ].map((key) => getFormStringList(formData, key))).map((row) => ({
      id: row[0] ?? "",
      name: row[1] ?? "",
      powerSourceRatingKva: row[2] ?? "",
      powerSourceFrequencyHz: row[3] ?? "",
      maximumPowerSourceVoltageV: row[4] ?? "",
      maximumPowerSourceCurrentA: row[5] ?? "",
      minimumPowerUtilization: row[6] ?? "",
      maximumPowerUtilization: row[7] ?? "",
    })),
    coilFrames: zipFormRows([
      "coilFrameId",
      "coilFrameName",
      "coilFrameSizeInches",
      "coilFrameInsideDiameterMm",
    ].map((key) => getFormStringList(formData, key))).map((row) => ({
      id: row[0] ?? "",
      name: row[1] ?? "",
      coilFrameSizeInches: row[2] ?? "",
      coilFrameInsideDiameterMm: row[3] ?? "",
    })),
    bottomEnds: zipFormRows([
      "bottomEndId",
      "bottomEndName",
      "numberOfTurns",
      "firstContactEdgeOffsetMm",
      "lastContactEdgeOffsetMm",
      "contactCenterSpacingsMm",
      "bottomEndWidthMm",
      "heatingResponseCelsiusMeterPerKwMinute",
    ].map((key) => getFormStringList(formData, key))).map((row) => ({
      id: row[0] ?? "",
      name: row[1] ?? "",
      numberOfTurns: row[2] ?? "",
      firstContactEdgeOffsetMm: row[3] ?? "",
      lastContactEdgeOffsetMm: row[4] ?? "",
      contactCenterSpacingsMm: row[5] ?? "",
      bottomEndWidthMm: row[6] ?? "",
      heatingResponseCelsiusMeterPerKwMinute: row[7] ?? "",
    })),
  };
}

function populateStandardEquipmentCatalogueForm(
  form: HTMLFormElement,
  catalogue: StandardEquipmentCatalogue,
): void {
  const powerSourcesBody = getRequiredTableSection(
    form,
    "#catalogue-power-sources",
  );
  const coilFramesBody = getRequiredTableSection(form, "#catalogue-coil-frames");
  const bottomEndsBody = getRequiredTableSection(form, "#catalogue-bottom-ends");

  powerSourcesBody.replaceChildren(
    ...catalogue.powerSources.map((powerSource) =>
      powerSourceTableRow({
        id: powerSource.id,
        name: powerSource.name,
        powerSourceRatingKva: String(powerSource.powerSourceRatingKva),
        powerSourceFrequencyHz: String(powerSource.powerSourceFrequencyHz),
        maximumPowerSourceVoltageV: String(powerSource.maximumPowerSourceVoltageV),
        maximumPowerSourceCurrentA: String(powerSource.maximumPowerSourceCurrentA),
        minimumPowerUtilization: String(
          powerSource.operatingLimits.minimumPowerUtilization,
        ),
        maximumPowerUtilization: String(
          powerSource.operatingLimits.maximumPowerUtilization,
        ),
      }),
    ),
  );

  coilFramesBody.replaceChildren(
    ...(catalogue.coilFrames ?? []).map((coilFrame) =>
      coilFrameTableRow({
        id: coilFrame.id,
        name: coilFrame.name,
        coilFrameSizeInches: String(coilFrame.coilFrameSizeInches),
        coilFrameInsideDiameterMm: String(coilFrame.coilFrameInsideDiameterMm),
      }),
    ),
  );

  bottomEndsBody.replaceChildren(
    ...(catalogue.bottomEnds ?? []).map((bottomEnd) =>
      bottomEndTableRow({
        id: bottomEnd.id,
        name: bottomEnd.name,
        numberOfTurns: String(bottomEnd.numberOfTurns),
        firstContactEdgeOffsetMm: String(bottomEnd.firstContactEdgeOffsetMm),
        lastContactEdgeOffsetMm: String(bottomEnd.lastContactEdgeOffsetMm),
        contactCenterSpacingsMm: bottomEnd.contactCenterSpacingsMm.join(", "),
        bottomEndWidthMm: String(bottomEnd.bottomEndWidthMm),
        heatingResponseCelsiusMeterPerKwMinute: String(
          bottomEnd.heatingResponseCelsiusMeterPerKwMinute,
        ),
      }),
    ),
  );
}

export function buildRecommendationResultView(
  recommendation: RecommendedSetup,
  activeOptionsInput: RecommendationOptionsInput,
): RecommendationResultView {
  const activeOptions = resolveDisplayRecommendationOptions(activeOptionsInput);

  return {
    statusLabel: titleCase(recommendation.recommendationStatus),
    statusClassName: statusClassName(recommendation.recommendationStatus),
    reason: recommendation.recommendationReason,
    summaryMetrics: [
      [
        "Power Source Rating",
        `${recommendation.selectedPowerSource.powerSourceRatingKva} kVA`,
      ],
      [
        "Power Source Frequency",
        `${recommendation.selectedPowerSource.powerSourceFrequencyHz} Hz`,
      ],
      [
        "Power Source Voltage",
        formatNullableNumber(
          recommendation.operatingElectricalValues?.powerSourceVoltageV ?? null,
          "V",
        ),
      ],
      [
        "Power Source Current",
        formatNullableNumber(
          recommendation.operatingElectricalValues?.powerSourceCurrentA ?? null,
          "A",
        ),
      ],
      [
        "Power Factor",
        recommendation.operatingElectricalValues === null
          ? "Not available"
          : String(recommendation.operatingElectricalValues.powerFactor),
      ],
      [
        "Delivered Power",
        formatNullableNumber(recommendation.deliveredPowerKw, "kW"),
      ],
      [
        "Power Utilization",
        formatPowerUtilization(recommendation.powerUtilization),
      ],
      [
        "Heating Cycle Time",
        formatNullableNumber(recommendation.heatingCycleTimeMinutes, "min"),
      ],
      [
        "Cooldown Time",
        formatOptionalNumber(recommendation.cooldownTimeMinutes, "min"),
      ],
    ],
    sections: {
      optimumCoil: [
    [
      "Number of turns",
      `Optimum ${recommendation.coilRecommendationComparison.optimum.numberOfTurns}; catalogue ${formatCatalogueComparisonNumber(
        recommendation.coilRecommendationComparison.catalogueMatch?.numberOfTurns,
      )}`,
    ],
    [
      "Turn spacing",
      `Optimum ${recommendation.coilRecommendationComparison.optimum.turnSpacingMm} mm; catalogue ${formatCatalogueComparisonNumber(
        recommendation.coilRecommendationComparison.catalogueMatch?.turnSpacingMm,
        "mm",
      )}`,
    ],
    [
      "Coil coverage",
      `Optimum ${recommendation.coilRecommendationComparison.optimum.coilCoverageMm} mm; catalogue ${formatCatalogueComparisonNumber(
        recommendation.coilRecommendationComparison.catalogueMatch?.coilCoverageMm,
        "mm",
      )}`,
    ],
    [
      "Catalogue coil section",
      formatNullableNumber(
        recommendation.coilRecommendationComparison.catalogueMatch?.coilSectionMm ??
          null,
        "mm",
      ),
    ],
    [
      "Catalogue heating length",
      formatNullableNumber(
        recommendation.coilRecommendationComparison.catalogueMatch
          ?.heatingLengthMm ?? null,
        "mm",
      ),
    ],
      ],
      selectedEquipment: [
    ["Power source", recommendation.selectedPowerSource.name],
    ["Power source rating", `${recommendation.selectedPowerSource.powerSourceRatingKva} kVA`],
    [
      "Power source frequency",
      `${recommendation.selectedPowerSource.powerSourceFrequencyHz} Hz`,
    ],
    [
      "Operating limits",
      `${formatPercent(
        recommendation.selectedPowerSource.operatingLimits.minimumPowerUtilization,
      )} to ${formatPercent(
        recommendation.selectedPowerSource.operatingLimits.maximumPowerUtilization,
      )}`,
    ],
    [
      "Output limiting",
      recommendation.operatingElectricalValues === null
        ? "Not available"
        : formatOutputLimiting(recommendation.operatingElectricalValues),
    ],
    [
      "Standard coil",
      recommendation.selectedCoilConfiguration?.name ?? "No standard coil selected",
    ],
    [
      "Coil frame",
      recommendation.selectedCoilConfiguration?.coilFrame?.name ?? "Not available",
    ],
    [
      "Bottom end",
      recommendation.selectedCoilConfiguration?.bottomEnd?.name ?? "Not available",
    ],
    [
      "Coil frame size",
      recommendation.selectedCoilConfiguration
        ? `${recommendation.selectedCoilConfiguration.coilFrameSizeMm} mm`
        : "Not available",
    ],
    [
      "Effective coil ID",
      formatOptionalNumber(
        recommendation.selectedCoilConfiguration?.effectiveCoilInsideDiameterMm,
        "mm",
      ),
    ],
    [
      "Turns and spacing",
      recommendation.selectedCoilConfiguration
        ? `${recommendation.selectedCoilConfiguration.numberOfTurns} turns at ${recommendation.selectedCoilConfiguration.turnSpacingMm} mm`
        : "Not available",
    ],
    [
      "Coil coverage",
      recommendation.selectedCoilConfiguration
        ? `${recommendation.selectedCoilConfiguration.coilCoverageMm} mm`
        : "Not available",
    ],
    [
      "Coil section",
      formatOptionalNumber(
        recommendation.selectedCoilConfiguration?.coilSectionMm,
        "mm",
      ),
    ],
    [
      "Heating length",
      formatOptionalNumber(
        recommendation.selectedCoilConfiguration?.heatingLengthMm,
        "mm",
      ),
    ],
      ],
      calculatedBasis: [
    [
      "Factory coating OD",
      `${recommendation.calculations.factoryCoatingOutsideDiameterMm} mm`,
    ],
    ["Bare steel length", `${recommendation.calculations.bareSteelLengthMm} mm`],
    [
      "Required coil ID",
      `${recommendation.calculations.requiredCoilInsideDiameterMm} mm`,
    ],
    [
      "Required coverage",
      `${recommendation.calculations.requiredCoilCoverageMm} mm`,
    ],
    [
      "Heating length",
      formatNullableNumber(recommendation.calculations.heatingLengthMm, "mm"),
    ],
      ],
      catalogueEvaluation: [
    [
      "Standard coils",
      String(recommendation.catalogueEvaluation.standardCoilConfigurationCount),
    ],
    [
      "Fitting coils",
      String(recommendation.catalogueEvaluation.fittingCoilConfigurationCount),
    ],
    [
      "Frame size fit",
      formatBoolean(recommendation.catalogueEvaluation.hasCoilFrameSizeFit),
    ],
    [
      "Coverage fit",
      formatBoolean(recommendation.catalogueEvaluation.hasCoilCoverageFit),
    ],
      ],
      advisoryCauses: advisoryCauseRows(recommendation.advisoryCauses),
      advisoryActions: advisoryActionRows(recommendation.advisoryActions),
      visibleAssumptions: [
    ["Reference frequency", `${activeOptions.referenceFrequencyHz} Hz`],
    ["Preferred turn spacing", `${activeOptions.preferredTurnSpacingMm} mm`],
    [
      "Turn bounds",
      `${activeOptions.turnBounds.minimumTurns} to ${activeOptions.turnBounds.maximumTurns}`,
    ],
    ["Coil clearance", `${recommendation.assumptions.coilClearanceMm} mm`],
    ["Required overhang", `${recommendation.assumptions.requiredOverhangMm} mm`],
    ["Tie bar diameter", `${activeOptions.tieBarDiameterMm} mm`],
    [
      "Frequency calibration",
      activeOptions.frequencyCalibrationCurve
        .map(
          (point) =>
            `${point.frequencyHz} Hz heat ${point.heatingCycleFactor}, cooldown ${point.cooldownFactor}`,
        )
        .join("; "),
    ],
    [
      "Low power utilization",
      formatPercent(activeOptions.advisoryThresholds.lowPowerUtilization),
    ],
    [
      "Frame margin",
      `${recommendation.assumptions.advisoryThresholds.minimumCoilFrameSizeMarginMm} mm`,
    ],
    [
      "Coverage margin",
      `${recommendation.assumptions.advisoryThresholds.minimumCoilCoverageMarginMm} mm`,
    ],
    [
      "Power boundary margin",
      String(
        recommendation.assumptions.advisoryThresholds
          .powerUtilizationBoundaryMargin,
      ),
    ],
    [
      "Power utilization reference",
      `${recommendation.assumptions.powerUtilizationEstimate.referenceFrequencyHz} Hz, ${recommendation.assumptions.powerUtilizationEstimate.referencePowerSourceVoltageV} V, ${recommendation.assumptions.powerUtilizationEstimate.referencePowerSourceCurrentA} A`,
    ],
    ["Power utilization model", "Empirical advisory estimate"],
    [
      "Starting temperature",
      `${recommendation.assumptions.heatingCycleEstimate.startingTemperatureC} C`,
    ],
    [
      "Starting source",
      recommendation.assumptions.heatingCycleEstimate.startingTemperatureSource,
    ],
    [
      "Heating model",
      recommendation.assumptions.heatingCycleEstimate.modelBasis,
    ],
    [
      "Heating frequency",
      `${recommendation.assumptions.heatingCycleEstimate.selectedPowerSourceFrequencyHz} Hz vs ${recommendation.assumptions.heatingCycleEstimate.referenceFrequencyHz} Hz, factor ${recommendation.assumptions.heatingCycleEstimate.frequencyFactor}`,
    ],
    [
      "Coil heating response",
      formatNullableNumber(
        recommendation.assumptions.heatingCycleEstimate
          .selectedCoilHeatingResponseCelsiusMeterPerKwMinute,
        "C m/kW min",
      ),
    ],
    [
      "Cooldown frequency",
      recommendation.assumptions.cooldownEstimate === undefined
        ? "Not available"
        : `${recommendation.assumptions.cooldownEstimate.selectedPowerSourceFrequencyHz} Hz vs ${recommendation.assumptions.cooldownEstimate.referenceFrequencyHz} Hz, factor ${recommendation.assumptions.cooldownEstimate.frequencyFactor}`,
    ],
    [
      "Heat soak inputs",
      recommendation.assumptions.cooldownEstimate === undefined
        ? "Not available"
        : `${recommendation.assumptions.cooldownEstimate.heatingCycleTimeMinutes} min heat, ${recommendation.assumptions.cooldownEstimate.pipeWallThicknessMm} mm wall, ${recommendation.assumptions.cooldownEstimate.temperatureDropC} C drop`,
    ],
    [
      "Heat soak factor",
      recommendation.assumptions.cooldownEstimate === undefined
        ? "Not available"
        : String(recommendation.assumptions.cooldownEstimate.heatSoakFactor),
    ],
      ],
    },
  };
}

function advisoryCauseRows(
  advisoryCauses: RecommendedSetup["advisoryCauses"],
): RecommendationResultRow[] {
  if (advisoryCauses.length === 0) {
    return [["Advisory causes", "None"]];
  }

  return advisoryCauses.map((cause) => {
    const details = [
      cause.message,
      cause.assumptionName === undefined
        ? undefined
        : `Assumption: ${cause.assumptionName}`,
      cause.outputLimitingReasons === undefined
        ? undefined
        : `Limited by ${cause.outputLimitingReasons.join(" and ")}`,
    ].filter((detail): detail is string => detail !== undefined);

    return [cause.code, details.join(" ")];
  });
}

function advisoryActionRows(
  advisoryActions: RecommendedSetup["advisoryActions"],
): RecommendationResultRow[] {
  if (advisoryActions.length === 0) {
    return [["Advisory actions", "None"]];
  }

  return advisoryActions.map((action) => [
    action.code,
    `${action.message} Cause codes: ${action.causeCodes.join(", ")}`,
  ]);
}

function populateRecommendationOptionsForm(
  form: HTMLFormElement,
  optionsInput: RecommendationOptionsInput,
): void {
  const options = resolveDisplayRecommendationOptions(optionsInput);
  const frequencyCalibrationBody = getRequiredTableSection(
    form,
    "#frequency-calibration-points",
  );

  setFormControlValue(form, "referenceFrequencyHz", options.referenceFrequencyHz);
  setFormControlValue(
    form,
    "preferredTurnSpacingMm",
    options.preferredTurnSpacingMm,
  );
  setFormControlValue(form, "minimumTurns", options.turnBounds.minimumTurns);
  setFormControlValue(form, "maximumTurns", options.turnBounds.maximumTurns);
  setFormControlValue(form, "optionCoilClearanceMm", options.coilClearanceMm);
  setFormControlValue(form, "optionRequiredOverhangMm", options.requiredOverhangMm);
  setFormControlValue(form, "tieBarDiameterMm", options.tieBarDiameterMm);
  setFormControlValue(
    form,
    "lowPowerUtilization",
    options.advisoryThresholds.lowPowerUtilization,
  );
  setFormControlValue(
    form,
    "minimumCoilFrameSizeMarginMm",
    options.advisoryThresholds.minimumCoilFrameSizeMarginMm,
  );
  setFormControlValue(
    form,
    "minimumCoilCoverageMarginMm",
    options.advisoryThresholds.minimumCoilCoverageMarginMm,
  );
  setFormControlValue(
    form,
    "powerUtilizationBoundaryMargin",
    options.advisoryThresholds.powerUtilizationBoundaryMargin,
  );

  frequencyCalibrationBody.replaceChildren(
    ...options.frequencyCalibrationCurve.map((point) =>
      calibrationTableRow(point),
    ),
  );

  for (const field of modelCoefficientFields) {
    setFormControlValue(
      form,
      field.key,
      options.advancedCalibration.modelCoefficients[field.key],
    );
  }
}

function setFormControlValue(
  form: HTMLFormElement,
  name: string,
  value: number,
): void {
  const control = form.elements.namedItem(name);
  if (control instanceof HTMLInputElement) {
    control.value = String(value);
  }
}

function initializeTabs(): void {
  const tabButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>("[data-tab-target]"),
  );

  for (const button of tabButtons) {
    button.addEventListener("click", () => {
      const targetId = button.dataset.tabTarget;
      if (targetId === undefined) {
        return;
      }

      for (const currentButton of tabButtons) {
        const isActive = currentButton === button;
        currentButton.setAttribute("aria-selected", String(isActive));
      }

      for (const panel of document.querySelectorAll<HTMLElement>("[data-tab-panel]")) {
        panel.hidden = panel.id !== targetId;
      }
    });
  }
}

function refreshCatalogueDraftState(form: HTMLFormElement): void {
  try {
    const draftCatalogue = buildStandardEquipmentCatalogueInput(
      readStandardEquipmentCatalogueFormValues(form),
    );
    updateCatalogueUnsavedState(
      hasUnsavedCatalogueChanges(draftCatalogue, savedStandardEquipmentCatalogue),
    );
    updateCatalogueMaintenancePreview(form);
    catalogueFeedback.hidden = true;
  } catch (error) {
    updateCatalogueUnsavedState(true);
    updateCatalogueMaintenancePreview(form);
    catalogueFeedback.hidden = false;
    catalogueFeedback.className = "options-feedback options-feedback-error";
    catalogueFeedback.textContent = formatError(error);
  }
}

function updateCatalogueMaintenancePreview(form: HTMLFormElement): void {
  const values = readStandardEquipmentCatalogueFormValues(form);

  for (const [index, row] of Array.from(
    form.querySelectorAll<HTMLTableRowElement>("#catalogue-bottom-ends tr"),
  ).entries()) {
    updateBottomEndDerivedCells(row, values.bottomEnds[index]);
  }

  try {
    const preview = buildCatalogueMaintenancePreview(values);
    generatedCatalogueCount.textContent = `Standard configurations: ${preview.generatedCatalogueCount}`;
  } catch {
    const rawCount = values.coilFrames.length * values.bottomEnds.length;
    generatedCatalogueCount.textContent = `Standard configurations: ${rawCount} pending valid data`;
  }
}

function updateBottomEndDerivedCells(
  row: HTMLTableRowElement,
  bottomEnd: CatalogueBottomEndFormValues | undefined,
): void {
  if (bottomEnd === undefined) {
    return;
  }

  const coilSectionCell = row.querySelector<HTMLElement>("[data-derived='coil-section']");
  const averageSpacingCell = row.querySelector<HTMLElement>(
    "[data-derived='average-contact-center-spacing']",
  );
  const generatedTurnSpacingCell = row.querySelector<HTMLElement>(
    "[data-derived='generated-turn-spacing']",
  );
  const derivedValues = deriveBottomEndFormValues(bottomEnd);

  if (coilSectionCell !== null) {
    coilSectionCell.textContent = formatOptionalNumber(
      derivedValues?.coilSectionMm,
      "mm",
    );
  }

  if (averageSpacingCell !== null) {
    averageSpacingCell.textContent = formatOptionalNumber(
      derivedValues?.averageContactCenterSpacingMm,
      "mm",
    );
  }

  if (generatedTurnSpacingCell !== null) {
    generatedTurnSpacingCell.textContent = formatOptionalNumber(
      derivedValues?.generatedTurnSpacingMm,
      "mm",
    );
  }
}

function powerSourceTableRow(
  powerSource: CataloguePowerSourceFormValues,
): HTMLTableRowElement {
  const row = tableRow([
    textInputCell("powerSourceId", powerSource.id),
    textInputCell("powerSourceName", powerSource.name),
    inputCell("powerSourceRatingKva", powerSource.powerSourceRatingKva, 0.1),
    inputCell("powerSourceFrequencyHz", powerSource.powerSourceFrequencyHz, 0.1),
    inputCell("maximumPowerSourceVoltageV", powerSource.maximumPowerSourceVoltageV, 0.1),
    inputCell("maximumPowerSourceCurrentA", powerSource.maximumPowerSourceCurrentA, 0.1),
    removeButtonCell("Remove power source"),
  ]);
  row.append(
    hiddenTableCell("minimumPowerUtilization", powerSource.minimumPowerUtilization),
    hiddenTableCell("maximumPowerUtilization", powerSource.maximumPowerUtilization),
  );
  return row;
}

function coilFrameTableRow(
  coilFrame: CatalogueCoilFrameFormValues,
): HTMLTableRowElement {
  return tableRow([
    textInputCell("coilFrameId", coilFrame.id),
    textInputCell("coilFrameName", coilFrame.name),
    inputCell("coilFrameSizeInches", coilFrame.coilFrameSizeInches, 0.1),
    inputCell("coilFrameInsideDiameterMm", coilFrame.coilFrameInsideDiameterMm, 0.1),
    removeButtonCell("Remove coil frame"),
  ]);
}

function bottomEndTableRow(
  bottomEnd: CatalogueBottomEndFormValues,
): HTMLTableRowElement {
  const derivedValues = deriveBottomEndFormValues(bottomEnd);

  const row = tableRow([
    textInputCell("bottomEndName", bottomEnd.name),
    inputCell("numberOfTurns", bottomEnd.numberOfTurns, 1),
    textInputCell("contactCenterSpacingsMm", bottomEnd.contactCenterSpacingsMm),
    inputCell("bottomEndWidthMm", bottomEnd.bottomEndWidthMm, 0.1),
    readOnlyCell(
      formatOptionalNumber(derivedValues?.coilSectionMm, "mm"),
      "coil-section",
    ),
    removeButtonCell("Remove bottom end"),
  ]);
  row.append(
    hiddenTableCell("bottomEndId", bottomEnd.id),
    hiddenTableCell("firstContactEdgeOffsetMm", bottomEnd.firstContactEdgeOffsetMm),
    hiddenTableCell("lastContactEdgeOffsetMm", bottomEnd.lastContactEdgeOffsetMm),
    hiddenTableCell("heatingResponseCelsiusMeterPerKwMinute", bottomEnd.heatingResponseCelsiusMeterPerKwMinute),
  );
  return row;
}

function tableRow(cells: HTMLElement[]): HTMLTableRowElement {
  const row = document.createElement("tr");
  row.append(...cells);
  return row;
}

function inputCell(
  name: string,
  value: string | number,
  step: number,
): HTMLTableCellElement {
  const cell = document.createElement("td");
  const input = document.createElement("input");
  input.name = name;
  input.type = "number";
  input.step = String(step);
  input.required = true;
  input.value = String(value);
  cell.append(input);
  return cell;
}

function textInputCell(name: string, value: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  const input = document.createElement("input");
  input.name = name;
  input.type = "text";
  input.required = true;
  input.value = value;
  cell.append(input);
  return cell;
}

function readOnlyCell(value: string, derivedKey?: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  cell.textContent = value;
  if (derivedKey !== undefined) {
    cell.dataset.derived = derivedKey;
  }
  return cell;
}

function hiddenTableCell(name: string, value: string | number): HTMLTableCellElement {
  const cell = document.createElement("td");
  cell.hidden = true;
  cell.setAttribute("aria-hidden", "true");
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = String(value);
  cell.append(input);
  return cell;
}

function removeButtonCell(label: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  const button = document.createElement("button");
  button.type = "button";
  button.className = "danger-button";
  button.dataset.removeCatalogueRow = "true";
  button.textContent = "Remove";
  button.setAttribute("aria-label", label);
  cell.append(button);
  return cell;
}

function calibrationRemoveButtonCell(): HTMLTableCellElement {
  const cell = document.createElement("td");
  const button = document.createElement("button");
  button.type = "button";
  button.className = "danger-button";
  button.dataset.removeCalibrationRow = "true";
  button.textContent = "Remove";
  button.setAttribute("aria-label", "Remove calibration point");
  cell.append(button);
  return cell;
}

function calibrationTableRow(point: FrequencyCalibrationPoint): HTMLTableRowElement {
  return tableRow([
    inputCell("frequencyCalibrationHz", point.frequencyHz, 0.1),
    inputCell("heatingCycleFactor", point.heatingCycleFactor, 0.001),
    inputCell("cooldownFactor", point.cooldownFactor, 0.001),
    calibrationRemoveButtonCell(),
  ]);
}

export function addFrequencyCalibrationPoint(form?: HTMLFormElement): void {
  if (typeof document === "undefined") {
    return;
  }

  const targetForm =
    form ??
    document.querySelector<HTMLFormElement>("#recommendation-options-form");

  if (targetForm === null) {
    return;
  }

  const tbody = targetForm.querySelector<HTMLTableSectionElement>(
    "#frequency-calibration-points",
  );

  if (tbody === null) {
    return;
  }

  tbody.append(
    calibrationTableRow({ frequencyHz: 0, heatingCycleFactor: 1, cooldownFactor: 1 }),
  );

  targetForm.dispatchEvent(new Event("input"));
}

function zipFormRows(columns: string[][]): string[][] {
  const rowCount = Math.max(0, ...columns.map((column) => column.length));
  return Array.from({ length: rowCount }, (_, rowIndex) =>
    columns.map((column) => column[rowIndex] ?? ""),
  );
}

function optionalNumberField<Key extends string>(
  key: Key,
  value: string,
): Record<Key, number> | Record<string, never> {
  const parsed = parseOptionalNumber(value);
  return parsed === undefined ? {} : { [key]: parsed } as Record<Key, number>;
}

function parseRequiredNumber(value: string, label: string): number {
  const parsed = Number(value);

  if (value.trim() === "" || !Number.isFinite(parsed)) {
    throw new Error(`${label} must be a finite number.`);
  }

  return parsed;
}

function parsePositiveNumber(value: string, label: string): number {
  const parsed = parseRequiredNumber(value, label);
  if (parsed <= 0) {
    throw new Error(`${label} must be greater than 0.`);
  }

  return parsed;
}

function parseNonNegativeNumber(value: string, label: string): number {
  const parsed = parseRequiredNumber(value, label);
  if (parsed < 0) {
    throw new Error(`${label} must be 0 or greater.`);
  }

  return parsed;
}

function parseUtilizationLimit(value: string, label: string): number {
  const parsed = parseNonNegativeNumber(value, label);
  if (parsed > 1) {
    throw new Error(`${label} must be less than or equal to 1.`);
  }

  return parsed;
}

function parsePositiveInteger(value: string, label: string): number {
  const parsed = parsePositiveNumber(value, label);
  if (!Number.isInteger(parsed)) {
    throw new Error(`${label} must be a whole number.`);
  }

  return parsed;
}

function parseContactCenterSpacings(value: string, label: string): number[] {
  const values = value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "");

  if (values.length === 0) {
    throw new Error(`${label} values must be provided.`);
  }

  return values.map((entry, index) =>
    parsePositiveNumber(entry, `${label} ${index + 1}`),
  );
}

function calculateBottomEndDerivedValues(
  bottomEnd: {
    bottomEndWidthMm: number;
    firstContactEdgeOffsetMm: number;
    lastContactEdgeOffsetMm: number;
    contactCenterSpacingsMm: number[];
  },
): CatalogueBottomEndDerivedValues {
  const coilSectionMm = calculateCoilSectionMm(bottomEnd);
  const averageContactCenterSpacingMm =
    calculateAverageContactCenterSpacingMm(bottomEnd);

  if (
    coilSectionMm === undefined ||
    averageContactCenterSpacingMm === undefined
  ) {
    throw new Error("Bottom end derived values require valid contact layout data.");
  }

  return {
    coilSectionMm,
    averageContactCenterSpacingMm,
    generatedTurnSpacingMm: averageContactCenterSpacingMm,
  };
}

function deriveBottomEndFormValues(
  bottomEnd: CatalogueBottomEndFormValues,
): CatalogueBottomEndDerivedValues | undefined {
  try {
    return calculateBottomEndDerivedValues({
      bottomEndWidthMm: parsePositiveNumber(
        bottomEnd.bottomEndWidthMm,
        "Bottom end width",
      ),
      firstContactEdgeOffsetMm: parseNonNegativeNumber(
        bottomEnd.firstContactEdgeOffsetMm,
        "First contact edge offset",
      ),
      lastContactEdgeOffsetMm: parseNonNegativeNumber(
        bottomEnd.lastContactEdgeOffsetMm,
        "Last contact edge offset",
      ),
      contactCenterSpacingsMm: parseContactCenterSpacings(
        bottomEnd.contactCenterSpacingsMm,
        "Contact center spacing",
      ),
    });
  } catch {
    return undefined;
  }
}

function calculateCoilSectionMm(bottomEnd: {
  bottomEndWidthMm: number;
  firstContactEdgeOffsetMm: number;
  lastContactEdgeOffsetMm: number;
}): number | undefined {
  const coilSectionMm =
    bottomEnd.bottomEndWidthMm -
    bottomEnd.firstContactEdgeOffsetMm -
    bottomEnd.lastContactEdgeOffsetMm;

  return Number.isFinite(coilSectionMm) && coilSectionMm > 0
    ? roundTo(coilSectionMm, 1)
    : undefined;
}

function calculateAverageContactCenterSpacingMm(bottomEnd: {
  contactCenterSpacingsMm: number[];
}): number | undefined {
  if (bottomEnd.contactCenterSpacingsMm.length === 0) {
    return undefined;
  }

  return roundTo(
    bottomEnd.contactCenterSpacingsMm.reduce(
      (sum, spacingMm) => sum + spacingMm,
      0,
    ) / bottomEnd.contactCenterSpacingsMm.length,
    1,
  );
}

function roundTo(value: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
}

function requireCatalogueText(value: string, label: string): string {
  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    throw new Error(`${label} must be provided.`);
  }

  return trimmedValue;
}

function rejectDuplicateCatalogueIds(
  items: Array<{ id: string }>,
  itemType: string,
): void {
  const seenIds = new Set<string>();

  for (const item of items) {
    if (seenIds.has(item.id)) {
      throw new Error(`Duplicate ${itemType} identity "${item.id}" is not allowed.`);
    }

    seenIds.add(item.id);
  }
}

function rejectNonIncreasingFrequencyCalibrationCurve(
  frequencyCalibrationCurve: FrequencyCalibrationPoint[],
): void {
  for (let index = 1; index < frequencyCalibrationCurve.length; index += 1) {
    const previousPoint = frequencyCalibrationCurve[index - 1];
    const currentPoint = frequencyCalibrationCurve[index];

    if (
      previousPoint !== undefined &&
      currentPoint !== undefined &&
      currentPoint.frequencyHz <= previousPoint.frequencyHz
    ) {
      throw new Error("Frequency calibration point frequencies must increase.");
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function defaultPowerSourceFormValues(
  rowNumber: number,
): CataloguePowerSourceFormValues {
  return {
    id: `ps-new-${rowNumber}`,
    name: `New power source ${rowNumber}`,
    powerSourceRatingKva: "60",
    powerSourceFrequencyHz: "480",
    maximumPowerSourceVoltageV: "400",
    maximumPowerSourceCurrentA: "87",
    minimumPowerUtilization: "0.55",
    maximumPowerUtilization: "0.85",
  };
}

function defaultCoilFrameFormValues(
  rowNumber: number,
): CatalogueCoilFrameFormValues {
  return {
    id: `frame-new-${rowNumber}`,
    name: `New coil frame ${rowNumber}`,
    coilFrameSizeInches: "30",
    coilFrameInsideDiameterMm: "750",
  };
}

function defaultBottomEndFormValues(
  rowNumber: number,
): CatalogueBottomEndFormValues {
  return {
    id: `bottom-end-new-${rowNumber}`,
    name: `New bottom end ${rowNumber}`,
    numberOfTurns: "6",
    firstContactEdgeOffsetMm: "52.5",
    lastContactEdgeOffsetMm: "52.5",
    contactCenterSpacingsMm: "75, 75, 75, 75, 75",
    bottomEndWidthMm: "480",
    heatingResponseCelsiusMeterPerKwMinute: "0.35",
  };
}

function removeFormRow<T>(rows: T[], rowIndex: number): T[] {
  return rows.filter((_, index) => index !== rowIndex);
}

function updateAssumptionsUnsavedState(hasUnsavedChanges: boolean): void {
  assumptionsUnsavedState.textContent = hasUnsavedChanges
    ? "Unsaved changes"
    : "Saved";
  assumptionsUnsavedState.className = hasUnsavedChanges
    ? "dirty-state dirty-state--unsaved"
    : "dirty-state";
}

function updateCatalogueUnsavedState(hasUnsavedChanges: boolean): void {
  catalogueUnsavedState.textContent = hasUnsavedChanges ? "Unsaved changes" : "Saved";
  catalogueUnsavedState.className = hasUnsavedChanges
    ? "dirty-state dirty-state--unsaved"
    : "dirty-state";
}

function buildPersistedApplicationState(): PersistedApplicationState {
  return {
    version: 1,
    standardEquipmentCatalogue: copyStandardEquipmentCatalogue(
      activeStandardEquipmentCatalogue,
    ),
    recommendationAssumptions: copyRecommendationOptions(
      resolveDisplayRecommendationOptions(activeRecommendationOptions),
    ),
    coilConfigurations: savedCoilConfigurations.map((configuration) => ({
      ...configuration,
      fieldJointGeometry: { ...configuration.fieldJointGeometry },
      heating: { ...configuration.heating },
      ...(configuration.applicationProduct === undefined
        ? {}
        : { applicationProduct: { ...configuration.applicationProduct } }),
      createdAt: configuration.createdAt.toISOString(),
    })),
    measurementRecords: savedMeasurementRecords.map((record) => ({
      ...record,
      loggedAt: record.loggedAt.toISOString(),
    })),
    updatedAt: new Date().toISOString(),
  };
}

function applyPersistedApplicationState(state: PersistedApplicationState): void {
  activeStandardEquipmentCatalogue = copyStandardEquipmentCatalogue(
    state.standardEquipmentCatalogue,
  );
  savedStandardEquipmentCatalogue = copyStandardEquipmentCatalogue(
    state.standardEquipmentCatalogue,
  );

  activeRecommendationOptions = copyRecommendationOptions(
    resolveDisplayRecommendationOptions(state.recommendationAssumptions),
  );
  savedRecommendationOptions = copyRecommendationOptions(
    resolveDisplayRecommendationOptions(state.recommendationAssumptions),
  );

  savedCoilConfigurations = state.coilConfigurations.map((configuration) => ({
    ...configuration,
    fieldJointGeometry: { ...configuration.fieldJointGeometry },
    heating: { ...configuration.heating },
    ...(configuration.applicationProduct === undefined
      ? {}
      : { applicationProduct: { ...configuration.applicationProduct } }),
    createdAt: new Date(configuration.createdAt),
  }));

  savedMeasurementRecords = state.measurementRecords.map((record) => ({
    ...record,
    loggedAt: new Date(record.loggedAt),
  }));
}

function assertPersistedApplicationState(
  state: unknown,
): asserts state is PersistedApplicationState {
  if (!isRecord(state) || state.version !== 1) {
    throw new Error("Hosted application state has an unsupported format.");
  }

  if (
    !isRecord(state.standardEquipmentCatalogue) ||
    !isRecord(state.recommendationAssumptions) ||
    !Array.isArray(state.coilConfigurations) ||
    !Array.isArray(state.measurementRecords)
  ) {
    throw new Error("Hosted application state is incomplete.");
  }
}

async function loadHostedApplicationState(): Promise<void> {
  if (typeof fetch === "undefined") {
    return;
  }

  const response = await fetch(HOSTED_STATE_ENDPOINT, {
    headers: {
      Accept: "application/json",
    },
    cache: "no-store",
  });

  if (response.status === 204 || response.status === 404) {
    return;
  }

  if (!response.ok) {
    throw new Error(
      `Hosted application state could not be loaded (${response.status}).`,
    );
  }

  const state: unknown = await response.json();
  assertPersistedApplicationState(state);
  applyPersistedApplicationState(state);
}

async function saveHostedApplicationState(): Promise<void> {
  if (typeof fetch === "undefined") {
    throw new Error("Hosted persistence is not available in this browser.");
  }

  const response = await fetch(HOSTED_STATE_ENDPOINT, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(buildPersistedApplicationState()),
  });

  if (response.status === 404 || response.status === 405) {
    throw new Error(
      "Hosted persistence endpoint is not available. Configure PUT /api/coil-configurator/state on the host.",
    );
  }

  if (!response.ok) {
    throw new Error(
      `Hosted persistence failed (${response.status}).`,
    );
  }
}

function persistApplicationStateAfterMutation(successMessage: string): void {
  void saveHostedApplicationState()
    .then(() => {
      showToast(successMessage);
    })
    .catch((error: unknown) => {
      showToast(`Not saved to host: ${formatError(error)}`, 5200);
    });
}

function parseOptionalNumber(value: string): number | undefined {
  if (value.trim() === "") {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Optional numeric value "${value}" must be finite.`);
  }

  return parsed;
}

export function resolveDisplayRecommendationOptions(
  optionsInput: RecommendationOptionsInput,
): RecommendationOptions {
  const modelCoefficients = {
    ...defaultRecommendationOptions.advancedCalibration.modelCoefficients,
    ...optionsInput.advancedCalibration?.modelCoefficients,
    ...optionsInput.modelCoefficients,
  };

  return {
    ...defaultRecommendationOptions,
    ...optionsInput,
    turnBounds: {
      ...defaultRecommendationOptions.turnBounds,
      ...optionsInput.turnBounds,
    },
    frequencyCalibrationCurve:
      optionsInput.frequencyCalibrationCurve?.map((point) => ({ ...point })) ??
      defaultRecommendationOptions.frequencyCalibrationCurve.map((point) => ({
        ...point,
      })),
    advisoryThresholds: {
      ...defaultRecommendationOptions.advisoryThresholds,
      ...optionsInput.advisoryThresholds,
    },
    advancedCalibration: {
      modelCoefficients,
    },
    modelCoefficients,
  };
}

function copyRecommendationOptions(
  options: RecommendationOptions,
): RecommendationOptionsInput {
  return {
    referenceFrequencyHz: options.referenceFrequencyHz,
    preferredTurnSpacingMm: options.preferredTurnSpacingMm,
    turnBounds: {
      ...options.turnBounds,
    },
    coilClearanceMm: options.coilClearanceMm,
    requiredOverhangMm: options.requiredOverhangMm,
    tieBarDiameterMm: options.tieBarDiameterMm,
    frequencyCalibrationCurve: options.frequencyCalibrationCurve.map((point) => ({
      ...point,
    })),
    advisoryThresholds: {
      ...options.advisoryThresholds,
    },
    advancedCalibration: {
      modelCoefficients: {
        ...options.advancedCalibration.modelCoefficients,
      },
    },
  };
}

function copyStandardEquipmentCatalogue(
  catalogue: StandardEquipmentCatalogue,
): StandardEquipmentCatalogue {
  return {
    powerSources: catalogue.powerSources.map((powerSource) => ({
      ...powerSource,
      operatingLimits: {
        ...powerSource.operatingLimits,
      },
    })),
    ...(catalogue.coilFrames === undefined
      ? {}
      : {
          coilFrames: catalogue.coilFrames.map((coilFrame) => ({
            ...coilFrame,
          })),
        }),
    ...(catalogue.bottomEnds === undefined
      ? {}
      : {
          bottomEnds: catalogue.bottomEnds.map((bottomEnd) => ({
            ...bottomEnd,
          })),
        }),
    ...(catalogue.coilConfigurations === undefined
      ? {}
      : {
          coilConfigurations: catalogue.coilConfigurations.map(
            (coilConfiguration) => ({
              ...coilConfiguration,
            }),
          ),
        }),
  };
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function getFormString(formData: FormData, key: string): string {
  const value = formData.get(key);
  return typeof value === "string" ? value : "";
}

function getFormStringList(formData: FormData, key: string): string[] {
  return formData
    .getAll(key)
    .map((value) => (typeof value === "string" ? value : ""));
}

function titleCase(value: string): string {
  return value
    .split(" ")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function statusClassName(status: RecommendedSetup["recommendationStatus"]): string {
  if (status === "recommended") {
    return "status-recommended";
  }

  if (status === "marginal") {
    return "status-marginal";
  }

  return "status-unsupported";
}

function formatNullableNumber(value: number | null, unit: string): string {
  return value === null ? "Not available" : `${value} ${unit}`;
}

function formatOptionalNumber(value: number | undefined, unit: string): string {
  return value === undefined ? "Not available" : `${value} ${unit}`;
}

function formatCatalogueComparisonNumber(
  value: number | undefined,
  unit = "",
): string {
  if (value === undefined) {
    return "Not available";
  }

  return unit === "" ? String(value) : `${value} ${unit}`;
}

function formatOutputLimiting(
  operatingElectricalValues: NonNullable<RecommendedSetup["operatingElectricalValues"]>,
): string {
  if (!operatingElectricalValues.outputLimited) {
    return "Not limited";
  }

  return `Limited by ${operatingElectricalValues.outputLimitingReasons.join(
    " and ",
  )}`;
}

function formatPowerUtilization(value: number | null): string {
  return value === null ? "Not available" : formatPercent(value);
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatBoolean(value: boolean): string {
  return value ? "Yes" : "No";
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function getRequiredElement(selector: string): HTMLElement {
  const element = document.querySelector<HTMLElement>(selector);

  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }

  return element;
}

function getRequiredTableSection(
  form: HTMLFormElement,
  selector: string,
): HTMLTableSectionElement {
  const element = form.querySelector<HTMLTableSectionElement>(selector);

  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }

  return element;
}

function initializePageHeader(): void {
  document.getElementById("glossary-button")?.addEventListener("click", () => {
    openGlossaryDrawer();
  });

  document.getElementById("create-config-button")?.addEventListener("click", () => {
    openConfigurationModal("create");
  });
}

export function openGlossaryDrawer(): void {
  if (typeof document === "undefined") {
    return;
  }

  const drawer = document.getElementById("glossary-drawer");
  const searchInput = document.getElementById("glossary-search") as HTMLInputElement | null;

  if (!drawer) {
    return;
  }

  populateGlossaryList("");

  if (searchInput) {
    searchInput.value = "";
  }

  drawer.classList.add("is-open");
  drawer.removeAttribute("aria-hidden");
  searchInput?.focus();
}

export function closeGlossaryDrawer(): void {
  if (typeof document === "undefined") {
    return;
  }

  const drawer = document.getElementById("glossary-drawer");

  if (!drawer) {
    return;
  }

  drawer.classList.remove("is-open");
  drawer.setAttribute("aria-hidden", "true");
}

function initializeGlossaryDrawer(): void {
  const drawer = document.getElementById("glossary-drawer");
  const backdrop = document.getElementById("glossary-backdrop");
  const closeButton = document.getElementById("glossary-close");
  const searchInput = document.getElementById("glossary-search") as HTMLInputElement | null;

  if (!drawer) {
    return;
  }

  backdrop?.addEventListener("click", () => {
    closeGlossaryDrawer();
  });

  closeButton?.addEventListener("click", () => {
    closeGlossaryDrawer();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawer.classList.contains("is-open")) {
      closeGlossaryDrawer();
    }
  });

  searchInput?.addEventListener("input", () => {
    populateGlossaryList(searchInput.value);
  });
}

function populateGlossaryList(query: string): void {
  const list = document.getElementById("glossary-list");

  if (!list) {
    return;
  }

  const normalizedQuery = query.trim().toLowerCase();
  const entries = Object.entries(GLOSSARY);
  const matching = normalizedQuery === ""
    ? entries
    : entries.filter(([, entry]) =>
        entry.title.toLowerCase().includes(normalizedQuery) ||
        entry.definition.toLowerCase().includes(normalizedQuery),
      );

  if (matching.length === 0) {
    list.replaceChildren(
      Object.assign(document.createElement("li"), {
        className: "glossary-drawer__empty",
        textContent: "No terms match your search.",
      }),
    );
    return;
  }

  const sorted = matching.slice().sort(([, a], [, b]) =>
    a.title.localeCompare(b.title),
  );

  list.replaceChildren(
    ...sorted.map(([, entry]) => {
      const item = document.createElement("li");
      item.className = "glossary-drawer__item";

      const term = document.createElement("p");
      term.className = "glossary-drawer__term";
      term.textContent = entry.title;

      const definition = document.createElement("p");
      definition.className = "glossary-drawer__definition";
      definition.textContent = entry.definition;

      item.append(term, definition);
      return item;
    }),
  );
}

let activePopoverButton: HTMLElement | null = null;

function initializeInfoAffordances(): void {
  if (typeof document === "undefined") {
    return;
  }

  const popover = document.getElementById("info-popover");

  if (!popover) {
    return;
  }

  document.addEventListener("mouseover", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>(".info-affordance");
    if (button !== null) {
      showInfoPopover(button, popover);
    }
  });

  document.addEventListener("mouseout", (event) => {
    const related = event.relatedTarget as HTMLElement | null;
    if (
      activePopoverButton !== null &&
      !activePopoverButton.contains(related) &&
      !popover.contains(related)
    ) {
      hideInfoPopover(popover);
    }
  });

  document.addEventListener("focusin", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>(".info-affordance");
    if (button !== null) {
      showInfoPopover(button, popover);
    }
  });

  document.addEventListener("focusout", (event) => {
    const related = event.relatedTarget as HTMLElement | null;
    if (
      activePopoverButton !== null &&
      !activePopoverButton.contains(related) &&
      !popover.contains(related)
    ) {
      hideInfoPopover(popover);
    }
  });

  document.addEventListener("click", (event) => {
    if (
      activePopoverButton !== null &&
      !(event.target as HTMLElement).closest(".info-affordance") &&
      !popover.contains(event.target as HTMLElement)
    ) {
      hideInfoPopover(popover);
    }
  });
}

function showInfoPopover(button: HTMLElement, popover: HTMLElement): void {
  const key = button.dataset.glossaryKey;

  if (key === undefined || key === "") {
    return;
  }

  const entry = GLOSSARY[key];

  if (entry === undefined) {
    return;
  }

  const titleEl = document.getElementById("info-popover-title");
  const definitionEl = document.getElementById("info-popover-definition");

  if (!titleEl || !definitionEl) {
    return;
  }

  titleEl.textContent = entry.title;
  definitionEl.textContent = entry.definition;

  popover.hidden = false;
  activePopoverButton = button;

  const rect = button.getBoundingClientRect();
  const popoverWidth = 280;
  const gap = 6;

  let left = rect.right + gap;
  if (left + popoverWidth > window.innerWidth - 8) {
    left = rect.left - popoverWidth - gap;
  }
  if (left < 8) {
    left = 8;
  }

  popover.style.top = `${Math.max(8, rect.top)}px`;
  popover.style.left = `${left}px`;
}

function hideInfoPopover(popover: HTMLElement): void {
  popover.hidden = true;
  activePopoverButton = null;
}

function initializeConfigurationsListView(): void {
  initializeStubConfigurations();
  renderConfigurationsList();

  document.getElementById("configuration-search")?.addEventListener("input", (event) => {
    configurationsSearchQuery = (event.target as HTMLInputElement).value;
    configurationsCurrentPage = 1;
    renderConfigurationsList();
  });

  const statusFilterButton = document.getElementById("status-filter-button");
  const statusFilterPopover = document.getElementById("status-filter-popover");

  statusFilterButton?.addEventListener("click", (event) => {
    event.stopPropagation();
    const isHidden = statusFilterPopover?.hidden ?? true;
    if (statusFilterPopover !== null && statusFilterPopover !== undefined) {
      statusFilterPopover.hidden = !isHidden;
      statusFilterButton.setAttribute("aria-expanded", String(!isHidden));
    }
  });

  document.addEventListener("click", (event) => {
    if (
      statusFilterPopover !== null &&
      statusFilterPopover !== undefined &&
      !statusFilterPopover.hidden &&
      !statusFilterPopover.contains(event.target as Node) &&
      event.target !== statusFilterButton
    ) {
      statusFilterPopover.hidden = true;
      statusFilterButton?.setAttribute("aria-expanded", "false");
    }
  });

  statusFilterPopover?.addEventListener("change", () => {
    const checkedBoxes = Array.from(
      statusFilterPopover.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked"),
    );
    configurationsStatusFilter = checkedBoxes.map((cb) => cb.value);
    configurationsCurrentPage = 1;
    renderConfigurationsList();
  });

  document.getElementById("configurations-back")?.addEventListener("click", () => {
    navigateToListView();
  });
}

function renderConfigurationsList(): void {
  const filtered = filterConfigurations(
    savedCoilConfigurations,
    configurationsSearchQuery,
    configurationsStatusFilter,
  );

  const countLabel = document.getElementById("configuration-count");
  if (countLabel !== null) {
    countLabel.textContent = `${filtered.length} configuration${filtered.length === 1 ? "" : "s"}`;
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / CONFIGURATIONS_PAGE_SIZE));
  if (configurationsCurrentPage > totalPages) {
    configurationsCurrentPage = totalPages;
  }

  const start = (configurationsCurrentPage - 1) * CONFIGURATIONS_PAGE_SIZE;
  const pageItems = filtered.slice(start, start + CONFIGURATIONS_PAGE_SIZE);

  const tbody = document.getElementById("configurations-tbody");
  const emptyState = document.getElementById("configurations-empty-state");
  const table = document.getElementById("configurations-table");

  if (tbody === null || emptyState === null || table === null) {
    return;
  }

  if (filtered.length === 0) {
    tbody.replaceChildren();
    emptyState.hidden = false;
    table.hidden = true;
  } else {
    emptyState.hidden = true;
    table.hidden = false;
    tbody.replaceChildren(...pageItems.map(configurationTableRow));
  }

  renderPaginationControls(filtered.length, configurationsCurrentPage);
}

function configurationTableRow(config: SavedCoilConfiguration): HTMLTableRowElement {
  const recommendation = config.recommendation;
  const status = recommendation.recommendationStatus;
  const turns = recommendation.selectedCoilConfiguration?.numberOfTurns ?? null;
  const utilization = recommendation.powerUtilization;

  const tr = document.createElement("tr");

  // REFERENCE
  const refCell = document.createElement("td");
  const refLink = document.createElement("a");
  refLink.href = "#";
  refLink.className = "config-reference-link";
  refLink.textContent = config.reference;
  refLink.addEventListener("click", (event) => {
    event.preventDefault();
    navigateToDetailView(config.id);
  });
  refCell.append(refLink);
  tr.append(refCell);

  // PIPE Ø
  const odCell = document.createElement("td");
  const odValue = document.createElement("span");
  odValue.className = "mono-value";
  odValue.textContent = `${config.fieldJointGeometry.pipeOutsideDiameterMm} mm`;
  odCell.append(odValue);
  tr.append(odCell);

  // POWER SOURCE
  const psCell = document.createElement("td");
  psCell.textContent = formatPowerSourceShortName(recommendation.selectedPowerSource.name);
  tr.append(psCell);

  // TURNS
  const turnsCell = document.createElement("td");
  turnsCell.className = "mono-value";
  turnsCell.textContent = turns !== null ? String(turns) : "—";
  tr.append(turnsCell);

  // STATUS
  const statusCell = document.createElement("td");
  const statusPill = document.createElement("span");
  statusPill.className = configurationStatusPillClass(status);
  const dot = document.createElement("span");
  dot.className = "status-pill__dot";
  statusPill.append(dot, " ", titleCase(status));
  statusCell.append(statusPill);
  tr.append(statusCell);

  // UTILIZATION
  const utilizationCell = document.createElement("td");
  utilizationCell.className = "mono-value";
  utilizationCell.textContent =
    utilization !== null ? formatPercent(utilization) : "—";
  tr.append(utilizationCell);

  // CREATED
  const createdCell = document.createElement("td");
  createdCell.textContent = formatConfigurationDate(config.createdAt);
  tr.append(createdCell);

  // ACTIONS
  const actionsCell = document.createElement("td");
  const actionsWrapper = document.createElement("div");
  actionsWrapper.className = "measurement-actions";

  const editButton = document.createElement("button");
  editButton.type = "button";
  editButton.className = "measurement-action-button";
  editButton.textContent = "Edit";
  editButton.setAttribute("aria-label", `Edit configuration ${config.reference}`);
  editButton.addEventListener("click", () => {
    openConfigurationModal("edit", config);
  });

  const deleteButton = document.createElement("button");
  deleteButton.type = "button";
  deleteButton.className = "measurement-action-button measurement-action-button--danger";
  deleteButton.textContent = "Delete";
  deleteButton.setAttribute("aria-label", `Delete configuration ${config.reference}`);
  deleteButton.addEventListener("click", () => {
    openDeleteConfigurationDialog(config.id);
  });

  actionsWrapper.append(editButton, deleteButton);
  actionsCell.append(actionsWrapper);
  tr.append(actionsCell);

  return tr;
}

function openDeleteConfigurationDialog(configId: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const config = savedCoilConfigurations.find((c) => c.id === configId);
  if (config === undefined) {
    return;
  }

  const body = document.createElement("p");
  body.textContent = `Delete configuration "${config.reference}"? This cannot be undone.`;

  showDialog("Delete Configuration", body, "Delete Configuration", () => {
    savedCoilConfigurations = savedCoilConfigurations.filter(
      (c) => c.id !== config.id,
    );
    renderConfigurationsList();
    persistApplicationStateAfterMutation("Configuration deleted and saved to host");
  });
}

function renderPaginationControls(total: number, currentPage: number): void {
  const container = document.getElementById("configurations-pagination");

  if (container === null) {
    return;
  }

  if (total <= CONFIGURATIONS_PAGE_SIZE) {
    container.replaceChildren();
    return;
  }

  const totalPages = Math.ceil(total / CONFIGURATIONS_PAGE_SIZE);
  const start = (currentPage - 1) * CONFIGURATIONS_PAGE_SIZE + 1;
  const end = Math.min(currentPage * CONFIGURATIONS_PAGE_SIZE, total);

  const showingLabel = document.createElement("span");
  showingLabel.className = "pagination-showing";
  showingLabel.textContent = `Showing ${start}–${end} of ${total}`;

  const controls = document.createElement("div");
  controls.className = "pagination-pages";

  const prevButton = document.createElement("button");
  prevButton.type = "button";
  prevButton.className = "secondary-button pagination-btn";
  prevButton.textContent = "‹";
  prevButton.setAttribute("aria-label", "Previous page");
  prevButton.disabled = currentPage === 1;
  prevButton.addEventListener("click", () => {
    configurationsCurrentPage -= 1;
    renderConfigurationsList();
  });
  controls.append(prevButton);

  for (let page = 1; page <= totalPages; page += 1) {
    const pageButton = document.createElement("button");
    pageButton.type = "button";
    pageButton.className =
      page === currentPage
        ? "primary-button pagination-btn pagination-btn--active"
        : "secondary-button pagination-btn";
    pageButton.textContent = String(page);
    pageButton.setAttribute("aria-label", `Page ${page}`);
    if (page === currentPage) {
      pageButton.setAttribute("aria-current", "page");
    }

    const capturedPage = page;
    pageButton.addEventListener("click", () => {
      configurationsCurrentPage = capturedPage;
      renderConfigurationsList();
    });
    controls.append(pageButton);
  }

  const nextButton = document.createElement("button");
  nextButton.type = "button";
  nextButton.className = "secondary-button pagination-btn";
  nextButton.textContent = "›";
  nextButton.setAttribute("aria-label", "Next page");
  nextButton.disabled = currentPage === totalPages;
  nextButton.addEventListener("click", () => {
    configurationsCurrentPage += 1;
    renderConfigurationsList();
  });
  controls.append(nextButton);

  container.replaceChildren(showingLabel, controls);
}

function navigateToListView(): void {
  const listView = document.getElementById("configurations-list-view");
  const detailView = document.getElementById("configurations-detail-view");

  if (listView !== null) {
    listView.hidden = false;
  }

  if (detailView !== null) {
    detailView.hidden = true;
  }

  currentDetailConfigId = null;
  selectConfigurationsTab();
  renderConfigurationsList();
}

function navigateToDetailView(configId: string): void {
  const config = savedCoilConfigurations.find((c) => c.id === configId);

  if (config === undefined) {
    return;
  }

  currentDetailConfigId = configId;

  const listView = document.getElementById("configurations-list-view");
  const detailView = document.getElementById("configurations-detail-view");

  if (listView !== null) {
    listView.hidden = true;
  }

  if (detailView !== null) {
    detailView.hidden = false;
  }

  deselectAllTabs();
  renderDetailStatusBanner(config);
  renderDetailAdvisorySection(config.recommendation);
  renderDetailOperatingPoint(config.recommendation);
  renderDetailCoilComparison(config.recommendation);
  renderCoilLayoutSchematic(config.recommendation, config);
  renderDetailCalculatedBasis(config.recommendation);

}

function deselectAllTabs(): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-tab-target]")) {
    button.setAttribute("aria-selected", "false");
  }
}

function selectConfigurationsTab(): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-tab-target]")) {
    const isConfigurations = button.dataset.tabTarget === "configurations-panel";
    button.setAttribute("aria-selected", String(isConfigurations));
  }
}

function renderDetailStatusBanner(config: SavedCoilConfiguration): void {
  const banner = document.getElementById("detail-status-banner");
  const referenceName = document.getElementById("detail-reference-name");
  const projectName = document.getElementById("detail-project-name");
  const statusPill = document.getElementById("detail-status-pill");
  const statusPillLabel = document.getElementById("detail-status-pill-label");

  if (banner !== null) {
    banner.className = detailStatusBannerClass(config.recommendation.recommendationStatus);
  }

  if (referenceName !== null) {
    referenceName.textContent = config.reference;
  }

  if (projectName !== null) {
    projectName.textContent = config.project !== "" ? config.project : "";
    projectName.hidden = config.project === "";
  }

  if (statusPill !== null) {
    statusPill.className = configurationStatusPillClass(
      config.recommendation.recommendationStatus,
    );
  }

  if (statusPillLabel !== null) {
    statusPillLabel.textContent = titleCase(config.recommendation.recommendationStatus);
  }
}

function renderDetailAdvisorySection(recommendation: RecommendedSetup): void {
  const causesList = document.getElementById("detail-advisory-causes-list");
  const actionsList = document.getElementById("detail-advisory-actions-list");

  if (causesList !== null) {
    renderDetailAdvisoryList(
      causesList,
      recommendation.advisoryCauses.map((c) => ({ code: c.code, message: c.message })),
      "No advisory causes",
    );
  }

  if (actionsList !== null) {
    renderDetailAdvisoryList(
      actionsList,
      recommendation.advisoryActions.map((a) => ({ code: a.code, message: a.message })),
      "No advisory actions",
    );
  }
}

function renderDetailOperatingPoint(recommendation: RecommendedSetup): void {
  const view = buildDetailOperatingPointView(recommendation);

  setDetailText("detail-ps-rating", view.ratedLimits.powerSourceRatingKva);
  setDetailText("detail-ps-frequency", view.ratedLimits.powerSourceFrequencyHz);
  setDetailText("detail-max-voltage", view.ratedLimits.maximumPowerSourceVoltageV);
  setDetailText("detail-max-current", view.ratedLimits.maximumPowerSourceCurrentA);

  setDetailText("detail-op-voltage", view.operatingPoint.powerSourceVoltageV);
  setDetailText("detail-op-current", view.operatingPoint.powerSourceCurrentA);
  setDetailText("detail-power-factor", view.operatingPoint.powerFactor);
  setDetailText("detail-delivered-power", view.operatingPoint.deliveredPowerKw);

  setDetailText("detail-utilization-value", view.utilization.displayValue);

  const fill = document.getElementById("detail-utilization-fill");
  if (fill !== null) {
    fill.className = `utilization-meter__fill ${view.utilization.fillClass}`.trim();
    fill.style.width =
      view.utilization.fillPercent !== null
        ? `${view.utilization.fillPercent}%`
        : "0%";
  }

  setDetailText("detail-heating-cycle-time", view.timing.heatingCycleTimeMinutes);
  setDetailText(
    "detail-cooldown-time",
    view.timing.cooldownTimeMinutes ?? "—",
  );
}

function setDetailText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el !== null) {
    el.textContent = text;
  }
}

function renderDetailCalculatedBasis(recommendation: RecommendedSetup): void {
  const basisView = buildDetailCalculatedBasisView(recommendation);
  setDetailText("detail-basis-coating-od", basisView.factoryCoatingOutsideDiameterMm);
  setDetailText("detail-basis-bare-steel", basisView.bareSteelLengthMm);
  setDetailText("detail-basis-heating-length", basisView.heatingLengthMm);
  setDetailText("detail-basis-target-temp", basisView.targetHeatTemperatureC);
  setDetailText("detail-basis-freq-factor", basisView.frequencyHeatingFactor);

  const evalView = buildDetailCatalogueEvaluationView(recommendation);
  setDetailText("detail-eval-generated-count", evalView.standardConfigurationsGenerated);
  setDetailText("detail-eval-fitting-count", evalView.configurationsThatFit);
  setDetailText("detail-eval-selected-id", evalView.configurationSelected);
  setDetailText("detail-eval-custom-review", evalView.customCoilDesignReviewAdvised);
}

function renderDetailCoilComparison(recommendation: RecommendedSetup): void {
  const view = buildDetailCoilComparisonView(recommendation);

  setDetailText("detail-optimum-turns", view.optimum.numberOfTurns);
  setDetailText("detail-catalogue-turns", view.catalogueMatch.numberOfTurns);
  setDetailText("detail-optimum-turn-spacing", view.optimum.turnSpacingMm);
  setDetailText("detail-catalogue-turn-spacing", view.catalogueMatch.turnSpacingMm);
  setDetailText("detail-optimum-coil-coverage", view.optimum.coilCoverageMm);
  setDetailText("detail-catalogue-coil-coverage", view.catalogueMatch.coilCoverageMm);
  setDetailText("detail-optimum-coil-section", view.optimum.coilSectionMm);
  setDetailText("detail-catalogue-coil-section", view.catalogueMatch.coilSectionMm);
  setDetailText("detail-optimum-heating-length", view.optimum.heatingLengthMm);
  setDetailText("detail-catalogue-heating-length", view.catalogueMatch.heatingLengthMm);

  const equipView = buildDetailEquipmentCardView(recommendation);
  setDetailText("detail-equip-power-source", equipView.powerSourceName);
  setDetailText("detail-equip-coil-id", equipView.coilIdentifier);
  setDetailText("detail-equip-frame-size", equipView.coilFrameSizeInches);
  setDetailText("detail-equip-effective-id", equipView.effectiveCoilInsideDiameterMm);
  setDetailText("detail-equip-turns", equipView.numberOfTurns);
  setDetailText("detail-equip-turn-spacing", equipView.turnSpacingMm);
  setDetailText("detail-equip-coil-coverage", equipView.coilCoverageMm);
  setDetailText("detail-equip-coil-section", equipView.coilSectionMm);

  const limitingEl = document.getElementById("detail-equip-output-limiting");
  if (limitingEl !== null) {
    limitingEl.textContent = equipView.outputLimitingLabel;
    limitingEl.className = equipView.outputLimitingApplied
      ? "detail-equipment-output-limiting--applied"
      : "";
  }
}

function renderCoilLayoutSchematic(
  recommendation: RecommendedSetup,
  config: SavedCoilConfiguration,
): void {
  const container = document.getElementById("detail-schematic-svg-container");
  if (container === null) {
    return;
  }

  const props = buildCoilLayoutSvgProps(recommendation, config);
  if (props === null) {
    container.innerHTML = "";
    return;
  }

  container.innerHTML = createCoilLayoutSvg(props);
}

function renderDetailAdvisoryList(
  listEl: HTMLElement,
  items: Array<{ code: string; message: string }>,
  emptyMessage: string,
): void {
  if (items.length === 0) {
    const empty = document.createElement("li");
    empty.className = "detail-advisory-empty";
    empty.textContent = emptyMessage;
    listEl.replaceChildren(empty);
    return;
  }

  listEl.replaceChildren(
    ...items.map((item) => {
      const li = document.createElement("li");
      li.className = "detail-advisory-item";

      const code = document.createElement("p");
      code.className = "detail-advisory-item__code";
      code.textContent = item.code;

      const message = document.createElement("p");
      message.className = "detail-advisory-item__message";
      message.textContent = item.message;

      li.append(code, message);
      return li;
    }),
  );
}

function initializeDetailView(): void {
  document.getElementById("detail-edit-button")?.addEventListener("click", () => {
    if (currentDetailConfigId === null) {
      return;
    }

    const config = savedCoilConfigurations.find((c) => c.id === currentDetailConfigId);

    if (config !== undefined) {
      openConfigurationModal("edit", config);
    }
  });

  document.getElementById("detail-recalculate-button")?.addEventListener("click", () => {
    showDialog(
      "Recalculate",
      "Recalculating will update the recommendation using the current catalogue data and assumptions.",
      "Recalculate",
      () => {
        if (currentDetailConfigId === null) {
          return;
        }

        const config = savedCoilConfigurations.find((c) => c.id === currentDetailConfigId);

        if (config === undefined) {
          return;
        }

        try {
          const recommendInput: RecommendSetupInput = {
            selectedPowerSourceId: config.selectedPowerSourceId,
            fieldJointGeometry: { ...config.fieldJointGeometry },
            heating: { ...config.heating },
            ...(config.applicationProduct !== undefined
              ? { applicationProduct: { ...config.applicationProduct } }
              : {}),
            recommendationOptions: activeRecommendationOptions,
            standardEquipmentCatalogue: activeStandardEquipmentCatalogue,
          };

          const newRecommendation = recommendSetup(recommendInput);
          const index = savedCoilConfigurations.findIndex(
            (c) => c.id === currentDetailConfigId,
          );

          if (index !== -1) {
            const updatedConfig = { ...config, recommendation: newRecommendation };
            savedCoilConfigurations[index] = updatedConfig;
            renderDetailStatusBanner(updatedConfig);
            renderDetailAdvisorySection(newRecommendation);
            renderDetailOperatingPoint(newRecommendation);
            renderDetailCoilComparison(newRecommendation);
            renderCoilLayoutSchematic(newRecommendation, updatedConfig);
            renderDetailCalculatedBasis(newRecommendation);
          }

          persistApplicationStateAfterMutation(
            "Configuration recalculated and saved to host",
          );
        } catch (error) {
          showToast(`Recalculation failed: ${formatError(error)}`);
        }
      },
    );
  });
}

function initializeConfigurationModal(): void {
  const modalScrim = document.getElementById("configuration-modal-scrim");
  const cancelButton = document.getElementById("configuration-modal-cancel");
  const closeButton = document.getElementById("configuration-modal-close");
  const submitButton = document.getElementById("configuration-modal-submit");

  modalScrim?.addEventListener("click", (event) => {
    if (event.target === modalScrim) {
      closeConfigurationModal();
    }
  });

  cancelButton?.addEventListener("click", () => {
    closeConfigurationModal();
  });

  closeButton?.addEventListener("click", () => {
    closeConfigurationModal();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && modalScrim !== null && !modalScrim.hidden) {
      closeConfigurationModal();
    }
  });

  submitButton?.addEventListener("click", () => {
    submitConfigurationModal();
  });

  initializeModalUnitToggles();
}

function initializeModalUnitToggles(): void {
  const toggleContainers = document.querySelectorAll<HTMLElement>(".unit-toggle");

  for (const container of Array.from(toggleContainers)) {
    const buttons = Array.from(
      container.querySelectorAll<HTMLButtonElement>(".unit-toggle__option"),
    );

    for (const button of buttons) {
      button.addEventListener("click", () => {
        const newUnit = button.dataset.toUnit as GeometryUnit | CoatingUnit | undefined;

        if (newUnit === undefined) {
          return;
        }

        const oldUnit = (container.dataset.unit ?? "mm") as GeometryUnit | CoatingUnit;
        const input = container.previousElementSibling as HTMLInputElement | null;

        if (input !== null && input.value !== "") {
          const valueMm = convertToMm(Number(input.value), oldUnit);
          const newValue = convertFromMm(valueMm, newUnit);
          input.value = String(parseFloat(newValue.toFixed(6)));
        }

        container.dataset.unit = newUnit;

        for (const btn of buttons) {
          btn.classList.toggle("unit-toggle__option--active", btn === button);
        }
      });
    }
  }
}

export function openConfigurationModal(mode: "create" | "edit", editConfig?: SavedCoilConfiguration): void {
  if (typeof document === "undefined") {
    return;
  }

  configurationModalMode = mode;
  configurationModalEditId = editConfig?.id ?? null;

  const scrim = document.getElementById("configuration-modal-scrim");
  const title = document.getElementById("configuration-modal-title");
  const submitButton = document.getElementById("configuration-modal-submit");
  const form = document.getElementById("configuration-modal-form") as HTMLFormElement | null;
  const errorsContainer = document.getElementById("configuration-modal-errors");
  const modalPowerSource = document.getElementById("modal-power-source") as HTMLSelectElement | null;

  if (scrim === null || title === null || submitButton === null) {
    return;
  }

  if (mode === "create") {
    title.textContent = "Create Coil Configuration";
    submitButton.textContent = "Create Configuration";
    form?.reset();
    clearModalErrors();
  } else if (editConfig !== undefined) {
    title.textContent = "Edit Coil Configuration";
    submitButton.textContent = "Save Changes";
    populateModalFromConfig(editConfig);
    clearModalErrors();
  }

  if (errorsContainer !== null) {
    errorsContainer.hidden = true;
  }

  if (modalPowerSource !== null) {
    populatePowerSourceOptions(modalPowerSource, activeStandardEquipmentCatalogue);
    if (editConfig !== undefined) {
      modalPowerSource.value = editConfig.selectedPowerSourceId;
    }
  }

  scrim.hidden = false;
  scrim.removeAttribute("aria-hidden");

  document.getElementById("modal-config-reference")?.focus();
}

export function closeConfigurationModal(): void {
  if (typeof document === "undefined") {
    return;
  }

  const scrim = document.getElementById("configuration-modal-scrim");

  if (scrim !== null) {
    scrim.hidden = true;
    scrim.setAttribute("aria-hidden", "true");
  }
}

function populateModalFromConfig(config: SavedCoilConfiguration): void {
  const setInputValue = (id: string, value: string | number): void => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el !== null) {
      el.value = String(value);
    }
  };

  setInputValue("modal-config-reference", config.reference);
  setInputValue("modal-config-project", config.project);
  setInputValue("modal-pipe-od", config.fieldJointGeometry.pipeOutsideDiameterMm);
  setInputValue("modal-wall-thickness", config.fieldJointGeometry.pipeWallThicknessMm);
  setInputValue("modal-coating-thickness", config.fieldJointGeometry.factoryCoatingThicknessMm * 1000);
  setInputValue("modal-side-a-cutback", config.fieldJointGeometry.sideACutbackMm);
  setInputValue("modal-side-b-cutback", config.fieldJointGeometry.sideBCutbackMm);
  setInputValue("modal-ambient-temp", config.heating.ambientTemperatureC);
  setInputValue("modal-starting-temp", config.heating.startingTemperatureC ?? "");
  setInputValue("modal-target-temp", config.heating.targetHeatTemperatureC);
  setInputValue("modal-app-product", config.applicationProduct?.name ?? "");
  setInputValue(
    "modal-subsequent-temp",
    config.applicationProduct?.subsequentProcessTemperatureC ?? "",
  );
}

function clearModalErrors(): void {
  const errorIds = [
    "configReference-error",
    "modalPipeOutsideDiameter-error",
    "modalPipeWallThickness-error",
    "modalFactoryCoatingThickness-error",
    "modalSideACutback-error",
    "modalSideBCutback-error",
    "modalAmbientTemperatureC-error",
    "modalTargetHeatTemperatureC-error",
  ];

  for (const id of errorIds) {
    const el = document.getElementById(id);
    if (el !== null) {
      el.hidden = true;
    }
  }
}

function showModalErrors(errors: Record<string, string>): void {
  clearModalErrors();

  for (const [field, message] of Object.entries(errors)) {
    const el = document.getElementById(`${field}-error`);
    if (el !== null) {
      el.textContent = message;
      el.hidden = false;
    }
  }
}

function readConfigurationModalFormValues(): ConfigurationModalFormValues {
  const getInputValue = (id: string): string => {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    return el?.value ?? "";
  };

  const getUnitToggleUnit = (toggleId: string): GeometryUnit | CoatingUnit => {
    const toggle = document.getElementById(toggleId);
    return (toggle?.dataset.unit ?? "mm") as GeometryUnit | CoatingUnit;
  };

  return {
    configReference: getInputValue("modal-config-reference"),
    configProject: getInputValue("modal-config-project"),
    modalSelectedPowerSourceId: getInputValue("modal-power-source"),
    modalPipeOutsideDiameter: getInputValue("modal-pipe-od"),
    modalPipeOutsideDiameterUnit: getUnitToggleUnit("modal-pipe-od-toggle") as GeometryUnit,
    modalPipeWallThickness: getInputValue("modal-wall-thickness"),
    modalPipeWallThicknessUnit: getUnitToggleUnit("modal-wall-thickness-toggle") as GeometryUnit,
    modalFactoryCoatingThickness: getInputValue("modal-coating-thickness"),
    modalFactoryCoatingThicknessUnit: getUnitToggleUnit("modal-coating-toggle") as CoatingUnit,
    modalSideACutback: getInputValue("modal-side-a-cutback"),
    modalSideACutbackUnit: getUnitToggleUnit("modal-side-a-toggle") as GeometryUnit,
    modalSideBCutback: getInputValue("modal-side-b-cutback"),
    modalSideBCutbackUnit: getUnitToggleUnit("modal-side-b-toggle") as GeometryUnit,
    modalAmbientTemperatureC: getInputValue("modal-ambient-temp"),
    modalStartingTemperatureC: getInputValue("modal-starting-temp"),
    modalTargetHeatTemperatureC: getInputValue("modal-target-temp"),
    modalApplicationProductName: getInputValue("modal-app-product"),
    modalSubsequentProcessTemperatureC: getInputValue("modal-subsequent-temp"),
  };
}

function submitConfigurationModal(): void {
  const values = readConfigurationModalFormValues();
  const errors = buildConfigurationModalErrors(values);

  if (Object.keys(errors).length > 0) {
    showModalErrors(errors);
    return;
  }

  try {
    const recommendInput = buildConfigurationModalInput(values, activeStandardEquipmentCatalogue);
    const recommendation = recommendSetup(recommendInput);

    const ambientTemperatureC = Number(values.modalAmbientTemperatureC);
    const startingTemperatureStr = values.modalStartingTemperatureC.trim();

    const newConfig: SavedCoilConfiguration = {
      id: configurationModalEditId ?? `config-${Date.now()}`,
      reference: values.configReference.trim(),
      project: values.configProject.trim(),
      selectedPowerSourceId: values.modalSelectedPowerSourceId,
      fieldJointGeometry: {
        pipeOutsideDiameterMm: convertToMm(
          Number(values.modalPipeOutsideDiameter),
          values.modalPipeOutsideDiameterUnit,
        ),
        pipeWallThicknessMm: convertToMm(
          Number(values.modalPipeWallThickness),
          values.modalPipeWallThicknessUnit,
        ),
        factoryCoatingThicknessMm: convertToMm(
          Number(values.modalFactoryCoatingThickness),
          values.modalFactoryCoatingThicknessUnit,
        ),
        sideACutbackMm: convertToMm(
          Number(values.modalSideACutback),
          values.modalSideACutbackUnit,
        ),
        sideBCutbackMm: convertToMm(
          Number(values.modalSideBCutback),
          values.modalSideBCutbackUnit,
        ),
      },
      heating: {
        ambientTemperatureC: Number.isFinite(ambientTemperatureC) ? ambientTemperatureC : 20,
        ...(startingTemperatureStr !== "" && Number.isFinite(Number(startingTemperatureStr))
          ? { startingTemperatureC: Number(startingTemperatureStr) }
          : {}),
        targetHeatTemperatureC: Number(values.modalTargetHeatTemperatureC),
      },
      recommendation,
      createdAt: new Date(),
    };

    const appProductName = values.modalApplicationProductName.trim();
    const subsequentTempStr = values.modalSubsequentProcessTemperatureC.trim();

    if (appProductName !== "") {
      newConfig.applicationProduct = {
        id: slugify(appProductName),
        name: appProductName,
        ...(subsequentTempStr !== "" && Number.isFinite(Number(subsequentTempStr))
          ? { subsequentProcessTemperatureC: Number(subsequentTempStr) }
          : {}),
      };
    }

    if (configurationModalMode === "edit" && configurationModalEditId !== null) {
      const index = savedCoilConfigurations.findIndex(
        (c) => c.id === configurationModalEditId,
      );

      if (index !== -1) {
        savedCoilConfigurations[index] = newConfig;
      }
    } else {
      savedCoilConfigurations.push(newConfig);
    }

    closeConfigurationModal();
    navigateToDetailView(newConfig.id);
    persistApplicationStateAfterMutation(
      configurationModalMode === "create"
        ? "Configuration created and saved to host"
        : "Configuration updated and saved to host",
    );
  } catch (error) {
    const errorsContainer = document.getElementById("configuration-modal-errors");

    if (errorsContainer !== null) {
      errorsContainer.hidden = false;
      errorsContainer.textContent = formatError(error);
    }
  }
}

function appendMeasurementRow(record: MeasurementRecord): void {
  const tbody = document.getElementById("measurements-tbody");
  const emptyState = document.getElementById("measurements-empty-state");

  if (tbody === null) {
    return;
  }

  if (emptyState !== null) {
    emptyState.hidden = true;
  }

  const predicted = record.predictedHeatingCycleTimeMinutes;
  const measured = record.measuredHeatingCycleTimeMinutes;

  const deltaPct =
    measured !== undefined && predicted !== null && predicted !== 0
      ? ((measured - predicted) / predicted) * 100
      : null;

  const deltaClass = deltaCycleClass(predicted, measured);

  const row = document.createElement("tr");
  row.dataset.measurementId = record.id;

  const dateCell = document.createElement("td");
  dateCell.textContent = formatConfigurationDate(record.loggedAt);
  row.append(dateCell);

  const configCell = document.createElement("td");
  configCell.textContent = record.configurationReference;
  row.append(configCell);

  const powerSourceCell = document.createElement("td");
  powerSourceCell.textContent = record.powerSourceLabel ?? "—";
  row.append(powerSourceCell);

  const predCell = document.createElement("td");
  predCell.className = "mono-value";
  predCell.textContent = predicted !== null ? `${predicted.toFixed(1)} min` : "—";
  row.append(predCell);

  const measCell = document.createElement("td");
  measCell.className = "mono-value";
  measCell.textContent =
    measured !== undefined ? `${measured.toFixed(1)} min` : "—";
  row.append(measCell);

  const deltaCell = document.createElement("td");
  deltaCell.className = ["mono-value", deltaClass].filter(Boolean).join(" ");
  deltaCell.textContent =
    deltaPct !== null
      ? `${deltaPct > 0 ? "+" : ""}${Math.round(deltaPct)}%`
      : "—";
  row.append(deltaCell);

  const tempCell = document.createElement("td");
  tempCell.className = "mono-value";
  tempCell.textContent = `${record.measuredTemperatureC} °C`;
  row.append(tempCell);

  const inFitCell = document.createElement("td");
  const inFitCheckbox = document.createElement("input");
  inFitCheckbox.type = "checkbox";
  inFitCheckbox.checked = record.inFit;
  inFitCheckbox.setAttribute("aria-label", "Include in calibration fit");
  inFitCheckbox.dataset.measurementId = record.id;
  inFitCheckbox.addEventListener("change", () => {
    const rec = savedMeasurementRecords.find((r) => r.id === record.id);
    if (rec !== undefined) {
      rec.inFit = inFitCheckbox.checked;
      updateMeasuredDataStatCards();
      persistApplicationStateAfterMutation("Measurement fit state saved to host");
    }
  });
  inFitCell.append(inFitCheckbox);
  row.append(inFitCell);

  const actionsCell = document.createElement("td");
  const actionsWrapper = document.createElement("div");
  actionsWrapper.className = "measurement-actions";

  const editButton = document.createElement("button");
  editButton.type = "button";
  editButton.className = "measurement-action-button";
  editButton.textContent = "Edit";
  editButton.setAttribute(
    "aria-label",
    `Edit measurement for ${record.configurationReference}`,
  );
  editButton.addEventListener("click", () => {
    openEditMeasurementDialog(record.id);
  });

  const deleteButton = document.createElement("button");
  deleteButton.type = "button";
  deleteButton.className =
    "measurement-action-button measurement-action-button--danger";
  deleteButton.textContent = "Delete";
  deleteButton.setAttribute(
    "aria-label",
    `Delete measurement for ${record.configurationReference}`,
  );
  deleteButton.addEventListener("click", () => {
    openDeleteMeasurementDialog(record.id);
  });

  actionsWrapper.append(editButton, deleteButton);
  actionsCell.append(actionsWrapper);
  row.append(actionsCell);

  tbody.append(row);
}

function renderMeasurementRows(): void {
  const tbody = document.getElementById("measurements-tbody");
  const emptyState = document.getElementById("measurements-empty-state");

  if (tbody === null) {
    return;
  }

  tbody.replaceChildren();

  if (emptyState !== null) {
    emptyState.hidden = savedMeasurementRecords.length > 0;
  }

  for (const record of savedMeasurementRecords) {
    appendMeasurementRow(record);
  }
}

function updateMeasuredDataStatCards(): void {
  const view = buildMeasuredDataAggregateView(savedMeasurementRecords);
  setDetailText("measured-record-count", String(view.recordCount));
  setDetailText("measured-mean-cycle-error", view.meanCycleTimeErrorPercent);
  setDetailText("measured-mean-power-error", view.meanDeliveredPowerErrorPercent);
  setDetailText("measured-last-logged", view.lastLoggedDate);
  const badgeEl = document.getElementById("measured-record-count-badge");
  if (badgeEl) {
    badgeEl.textContent = `${view.recordCount} records`;
  }
  updateReviewCalibrationButtonState();
}

function initializeMeasuredDataTab(): void {
  document.getElementById("log-measurement-button")?.addEventListener("click", () => {
    openLogMeasurementDialog();
  });

  initializeSeedMeasurementRecords();
  renderMeasurementRows();
  updateMeasuredDataStatCards();
}

function initializeDialog(): void {
  const scrim = document.getElementById("dialog-scrim");
  const cancelButton = document.getElementById("dialog-cancel");
  const ctaButton = document.getElementById("dialog-cta");

  scrim?.addEventListener("click", (event) => {
    if (event.target === scrim) {
      hideDialog();
    }
  });

  cancelButton?.addEventListener("click", () => {
    hideDialog();
  });

  ctaButton?.addEventListener("click", () => {
    const callback = dialogCtaCallback;
    if (callback !== undefined) {
      const result = callback();
      if (result !== false) {
        dialogCtaCallback = undefined;
        hideDialog();
      }
    } else {
      dialogCtaCallback = undefined;
      hideDialog();
    }
  });
}

export function showDialog(
  title: string,
  body: string | HTMLElement,
  ctaLabel = "Confirm",
  onConfirm?: () => boolean | void,
  options?: { disableCta?: boolean },
): void {
  if (typeof document === "undefined") {
    return;
  }

  const scrim = document.getElementById("dialog-scrim");
  const titleEl = document.getElementById("dialog-title");
  const bodyEl = document.getElementById("dialog-body");
  const ctaEl = document.getElementById("dialog-cta") as HTMLButtonElement | null;

  if (!scrim || !titleEl || !bodyEl || !ctaEl) {
    return;
  }

  dialogCtaCallback = onConfirm;
  titleEl.textContent = title;
  if (typeof body === "string") {
    bodyEl.innerHTML = `<p>${body}</p>`;
  } else {
    bodyEl.replaceChildren(body);
  }
  ctaEl.textContent = ctaLabel;
  ctaEl.disabled = options?.disableCta === true;
  scrim.hidden = false;
  scrim.removeAttribute("aria-hidden");
}

export function hideDialog(): void {
  if (typeof document === "undefined") {
    return;
  }

  const scrim = document.getElementById("dialog-scrim");

  if (!scrim) {
    return;
  }

  scrim.hidden = true;
  scrim.setAttribute("aria-hidden", "true");
}

export function showToast(message: string, duration = 2400): void {
  if (typeof document === "undefined") {
    return;
  }

  const container = document.getElementById("toast-container");

  if (!container) {
    return;
  }

  const toast = document.createElement("div");
  toast.className = "toast";
  toast.setAttribute("role", "status");

  const check = document.createElement("span");
  check.className = "toast__check";
  check.setAttribute("aria-hidden", "true");
  check.textContent = "✓";

  const text = document.createTextNode(message);

  toast.append(check, text);
  container.append(toast);

  setTimeout(() => {
    toast.classList.add("toast--dismissing");
    toast.addEventListener(
      "animationend",
      () => {
        toast.remove();
      },
      { once: true },
    );
  }, duration);
}
